import type { CardComponentProps } from '@i22team/td-longtail-vue-components';
import { detectDevice } from '@i22team/td-longtail-vue-components';
import { openModal } from '~/composables/modal';

export type PartnerCategory = 'all' | 'apps' | 'streaming' | 'mobil';

export type AnleitungBlock =
  | { type: 'paragraph'; html: string }
  | { type: 'subheading'; text: string }
  | { type: 'list'; items: string[] }
  | { type: 'hint'; html: string }
  | { type: 'faq'; items: { question: string; answer: string }[] };

export type AnleitungSection = {
  id: string;
  number: string;
  heading: string;
  blocks: AnleitungBlock[];
};

export type Anleitung = {
  eyebrow: string;
  title: string;
  titleAccent: string;
  lead: string;
  meta: { duration: string; device: string; confirmation: string };
  sections: AnleitungSection[];
};

export type Partner = {
  id?: 'apple-appstore' | 'google-playstore' | 'spotify-premium-telekom' | 'spotify-premium';
  cat: PartnerCategory;
  card: CardComponentProps;
  anleitung: Anleitung;
};

export type PartnerIntro = {
  key: PartnerCategory;
  label: string;
  card: CardComponentProps;
  list: string[];
  additional?: string;
};

export const openAnleitung = (partner: Partner) => {
  openModal({
    component: {
      is: defineAsyncComponent(() => import('~/components/dcb/landingpage/partner-anleitung.vue')),
      props: { anleitung: partner.anleitung, logo: partner.card.image },
    },
    noMaxWidth: true,
    className: 'dcb-lp-partners__anleitung-modal',
    closeOnBackdrop: true,
  });
};

export const tabs: PartnerIntro[] = [
  {
    key: 'all',
    label: 'Alle',
    card: {
      heading: {
        tag: 'h3',
        size: 'md',
        spacesBottomRem: true,
        text: 'Überall, wo du digital zahlst',
      },
      subtitle: {
        text: 'Streaming, Apps, Games und Mobilität: alle Partner auf einen Blick. Such dir aus, wo du per Telekom Handyrechnung zahlen willst.',
      },
      imagePosition: 'right',
      imageBackground: {
        src: '/content/content-images/dcb/landingpage/a3c7eda7ccf934568fb3cbb2ac054d2e/brand-kv-movie-1080.jpg',
        aspectRatio: '1080 / 1918',
        sizes: 'xs:90vw sm:50vw md:50vw lg:684px',
        lazy: true,
      },
    },
    list: [
      'Ein Bezahlweg für viele Dienste',
      'Kein Konto, keine Kartendaten nötig',
      'Transparent auf der Telekom Rechnung',
    ],
  },
  {
    key: 'streaming',
    label: 'Streaming & Musik',
    card: {
      heading: {
        tag: 'h3',
        size: 'md',
        spacesBottomRem: true,
        text: 'Filme, Serien & Musik streamen',
      },
      subtitle: {
        text: 'Netflix, Spotify oder Deezer? Bezahl deine Streaming-Abos direkt über die Telekom Handyrechnung, ohne Konto-Setup, ohne Kreditkarte.',
      },
      imagePosition: 'right',
      imageBackground: {
        src: '/content/content-images/dcb/landingpage/partner-section/8a8c8ef554374075a1ef68469e44b916/hero-streaming-photo.jpg',
        aspectRatio: '2000 / 1333',
      },
    },
    list: [
      'Je nach Anbieter: Checkout, Telekom Buchung oder App-Store',
      'Kündbar wie gewohnt beim Anbieter',
      'Transparent auf der Telekom Rechnung',
    ],
  },
  {
    key: 'apps',
    label: 'Apps & Games',
    card: {
      imagePosition: 'right',
      heading: {
        tag: 'h3',
        size: 'md',
        spacesBottomRem: true,
        text: 'Apps, In-App-Käufe & Game-Pässe',
      },
      subtitle: {
        text: 'Apps, Abos und Coins im App Store, Google Play oder im Game – alles über deine Telekom Mobilfunkrechnung, ohne hinterlegte Kreditkarte.',
      },
    },
    list: [
      'Kompatibel mit den großen App-Stores',
      'Kein Bank- oder Kartendaten-Hinterlegen',
      'Kindersicher per Drittanbietersperre als Limit',
    ],
    additional:
      '<span class="text--primary">Zum Hinterlegen findest du hier ein Video, das dich Schritt für Schritt führt.</span>',
  },

  {
    key: 'mobil',
    label: 'Mobilität',
    card: {
      heading: {
        tag: 'h3',
        size: 'md',
        spacesBottomRem: true,
        text: 'Parken, Bike & unterwegs zahlen',
      },
      subtitle: {
        text: 'Parkschein, Leihrad oder Ladesäule – zahl spontan unterwegs per Handyrechnung, ganz ohne Bargeld, Karte oder neues Konto.',
      },
      imagePosition: 'right',
      imageBackground: {
        src: '/content/content-images/dcb/landingpage/33c8e8cbd51deb2d0efb78c53766a33e/brand-kv-city-1080.jpg',
        aspectRatio: '320 /433',
        lazy: true,
      },
    },

    list: [
      'Spontan ohne Bargeld oder Karte',
      'Ein Bezahlweg für viele Mobilitäts-Apps',
      'Transparent auf der Telekom Rechnung',
    ],
  },
];

const cardHeading: CardComponentProps['heading'] = {
  tag: 'h4',
  size: 'sm',
  spacesBottomRem: true,
};

export const partners: Partner[] = [
  {
    cat: 'streaming',
    card: {
      heading: { ...cardHeading, text: 'Netflix' },
      text: 'Filme, Serien und Games streamen. Handyrechnung als Bezahlart im Konto hinterlegen.',
      buttons: [
        {
          tag: 'a',
          href: 'https://www.netflix.com/de',
          isExternal: true,
          label: 'Weiter zu Netflix',
          primary: true,
        },
      ],
      image: {
        src: '/content/content-images/dcb/messe/partner/00b1bcdacf4d730b05b9e1cbaaf43e7a/netflix-logo.svg',
        lazy: true,
        alt: 'Netflix Logo',
        aspectRatio: '178 / 48',
      },
    },
    anleitung: {
      eyebrow: 'Anleitung · Netflix',
      title: 'So zahlst du Netflix ',
      titleAccent: 'per Handyrechnung.',
      lead: 'Beim Netflix-Abo wählst du im Checkout „Zur Handyrechnung hinzufügen“ und bestätigst per SMS-Code. Die monatlichen Kosten erscheinen danach auf deiner Telekom Mobilfunkrechnung.',
      meta: {
        duration: 'Dauer: ca. 5 Minuten',
        device: 'Gerät: Browser oder Smartphone',
        confirmation: 'Sicher per SMS-Code',
      },
      sections: [
        {
          id: 'start',
          number: '1',
          heading: 'So funktioniert es bei Netflix',
          blocks: [
            {
              type: 'paragraph',
              html: 'Netflix bietet die Telekom Handyrechnung <strong>direkt im eigenen Checkout</strong> an. Du wählst dort die Zahlart <strong>„Zur Handyrechnung hinzufügen“</strong>, gibst deine Mobilfunknummer ein und bestätigst per SMS-Code. Die monatlichen Kosten erscheinen danach auf deiner <strong>Telekom Mobilfunkrechnung</strong>, dein Netflix-Konto bleibt das gleiche. Eine Kreditkarte oder ein PayPal-Login ist nicht nötig.',
            },
            { type: 'subheading', text: 'Du brauchst dazu:' },
            {
              type: 'list',
              items: [
                'Einen aktiven <strong>Telekom Mobilfunkvertrag</strong>.',
                'Dein Telekom-<strong>Smartphone griffbereit</strong>, weil du per SMS-Code bestätigst.',
                'Falls vorhanden: dein <strong>bestehendes Netflix-Konto</strong>. Du kannst die Handyrechnung auch nachträglich als Zahlart hinterlegen.',
              ],
            },
          ],
        },
        {
          id: 'checkout',
          number: '2',
          heading: 'Im Netflix-Checkout per Handyrechnung zahlen',
          blocks: [
            {
              type: 'paragraph',
              html: 'So zahlst du dein Netflix-Abo direkt im Netflix-Checkout per Handyrechnung:',
            },
            {
              type: 'list',
              items: [
                'Öffne <a href="https://www.netflix.com/de/" target="_blank" rel="noopener" class="link link--external"><strong>netflix.com</strong></a> und starte die Anmeldung für ein neues Abo, oder logge dich in dein <strong>bestehendes Konto</strong> ein.',
                'Wähle deinen Netflix-Plan: <strong>Standard mit Werbung</strong>, <strong>Standard</strong> oder <strong>Premium</strong>.',
                'Auf der Zahlungsseite wählst du unter den Zahlarten <strong>„Zur Handyrechnung hinzufügen“</strong>.',
                'Wähle als Anbieter <strong>Telekom</strong> (Mobile-Billing gibt es auch bei O2 und Vodafone) und gib deine <strong>Mobilfunknummer</strong> ein.',
                'Netflix schickt dir per SMS einen <strong>6-stelligen Bestätigungscode</strong>. Trage ihn ein, um die Zahlart zu bestätigen.',
                'Fertig. Netflix ist aktiv, die Kosten laufen ab sofort über deine <strong>Telekom Mobilfunkrechnung</strong>.',
              ],
            },
            {
              type: 'hint',
              html: '<b>Bestehendes Konto umstellen:</b> Hast du bisher mit Kreditkarte oder PayPal gezahlt? Dann öffnest du <strong>Konto → Zahlungsdetails verwalten</strong> auf netflix.com und wählst dort <strong>„Zur Handyrechnung hinzufügen“</strong>. Ab dem nächsten Abrechnungstermin läuft alles über die Telekom, du musst nichts kündigen.',
            },
          ],
        },
        {
          id: 'probleme',
          number: '3',
          heading: 'Häufige Fragen und Probleme',
          blocks: [
            {
              type: 'faq',
              items: [
                {
                  question: 'Ich bekomme keine SMS mit Code.',
                  answer:
                    'Prüfe, ob deine SIM aktiv ist und Empfang hat. Bei Prepaid kann die SMS bis zu zwei Minuten brauchen. Funktioniert es danach noch nicht, fordere den Code direkt erneut über den Link <strong>„Code erneut senden“</strong> an.',
                },
                {
                  question: 'Welche Anbieter werden im Netflix-Checkout unterstützt?',
                  answer:
                    'In Deutschland kannst du das Netflix-Abo über die <strong>Telekom</strong>, <strong>O2</strong> und <strong>Vodafone</strong> auf die Handyrechnung legen. Wähle bei der Anbieter-Abfrage einfach Telekom aus.',
                },
                {
                  question: 'Wo sehe ich den Netflix-Posten auf der Telekom Rechnung?',
                  answer:
                    'Du findest Netflix in <strong>MeinMagenta → Rechnungen → Drittanbieter-Leistungen</strong>, mit Datum und Betrag.',
                },
                {
                  question: 'Kann ich Netflix wieder kündigen?',
                  answer:
                    'Du kündigst wie gewohnt direkt bei Netflix unter <strong>Konto → Mitgliedschaft kündigen</strong>. Die Telekom Abrechnung stoppt danach automatisch.',
                },
              ],
            },
          ],
        },
      ],
    },
  },
  {
    id: 'spotify-premium-telekom',
    cat: 'streaming',
    card: {
      heading: { ...cardHeading, text: 'Spotify' },
      text: 'Spotify Premium buchen und bei ausgewählten Abos die Abrechnung per Handyrechnung wählen. Millionen Titel werbefrei.',
      buttons: [
        {
          tag: 'a',
          href: 'https://www.telekom.de/optionsuebersicht/mobilfunk/spotify',
          isExternal: true,
          label: 'Zur Telekom Buchung',
          primary: true,
        },
      ],
      image: {
        src: '/content/content-images/dcb/messe/partner/0e56026d9e356b3b3fad7f8e009678eb/logo-partner-spotify-premium.svg',
        lazy: true,
        alt: 'Spotify Premium Logo',
        aspectRatio: '441.7 / 111.9',
      },
    },
    anleitung: {
      eyebrow: 'Anleitung · Spotify',
      title: 'So bezahlst du Spotify ',
      titleAccent: 'per Handyrechnung.',
      lead: 'Spotify Premium direkt über deine Telekom Mobilfunkrechnung bezahlen, ohne Kreditkarte oder PayPal. Funktioniert für neue Accounts und für bestehende Abos.',
      meta: {
        duration: 'Dauer: ca. 4 Minuten',
        device: 'Gerät: Browser oder Smartphone',
        confirmation: 'Sicher per SMS-OTP',
      },
      sections: [
        {
          id: 'start',
          number: '1',
          heading: 'Bevor du startest',
          blocks: [
            {
              type: 'paragraph',
              html: 'Spotify Premium per Handyrechnung buchst du <strong>nicht im Spotify-Checkout</strong>, sondern direkt bei der Telekom. Du wählst die Spotify-Option in der Telekom Optionsübersicht, bestätigst per SMS-Code und die Abrechnung läuft jeden Monat <strong>über deine Telekom Mobilfunkrechnung</strong>. Eine Kreditkarte oder ein PayPal-Login ist dafür nicht nötig.',
            },
            { type: 'subheading', text: 'Du brauchst dazu:' },
            {
              type: 'list',
              items: [
                'Einen aktiven <strong>Telekom Mobilfunkvertrag</strong>.',
                'Dein Telekom-<strong>Smartphone griffbereit</strong>, weil du die Buchung per SMS-Code bestätigst.',
                'Optional: einen <strong>Spotify-Account</strong>. Wenn du noch keinen hast, legst du ihn bei der Aktivierung mit an.',
              ],
            },
            {
              type: 'hint',
              html: 'Wichtig: Ein Spotify-Premium-Abo kannst du jeweils nur einmal pro Konto buchen. Hast du bereits ein bei Spotify direkt bezahltes Abo, kündige es zum Laufzeitende, bevor du die Telekom Buchung mit demselben Konto verknüpfst, damit du nicht doppelt zahlst.',
            },
          ],
        },
        {
          id: 'buchen',
          number: '2',
          heading: 'Spotify über die Telekom buchen',
          blocks: [
            {
              type: 'paragraph',
              html: 'So buchst du Spotify Premium über die Telekom und zahlst per Handyrechnung:',
            },
            {
              type: 'list',
              items: [
                'Öffne die <a href="https://www.telekom.de/optionsuebersicht/mobilfunk/spotify" target="_blank" rel="noopener" class="link link--external">Optionsübersicht</a> im Browser oder über die MeinMagenta App.',
                'Wähle den gewünschten <strong>Spotify-Premium-Plan</strong> und starte die Buchung über die Telekom.',
                'Bestätige die einmalige <strong>Drittanbieter-Freigabe</strong> für deine Telekom Mobilfunknummer, falls du danach gefragt wirst.',
                'Du bekommst per SMS einen <strong>6-stelligen Bestätigungscode</strong>. Trage ihn ein, um die Buchung abzuschließen.',
                'Spotify Premium ist gebucht. Die Kosten erscheinen ab sofort als <strong>eigene Position auf deiner Telekom Mobilfunkrechnung</strong>, kein Sammel-Eintrag.',
              ],
            },
          ],
        },
        {
          id: 'konto',
          number: '3',
          heading: 'Spotify-Konto verknüpfen oder anlegen',
          blocks: [
            {
              type: 'paragraph',
              html: 'Nach der Buchung verbindest du die Premium-Mitgliedschaft mit deinem Spotify-Konto:',
            },
            {
              type: 'list',
              items: [
                'Folge dem <strong>Aktivierungslink</strong>, den die Telekom dir nach der Buchung anzeigt oder per E-Mail bzw. SMS schickt.',
                'Hast du noch <strong>kein Spotify-Konto</strong>, legst du es jetzt an: E-Mail-Adresse, Passwort und Geburtsdatum eingeben. Hast du bereits eins, <strong>loggst du dich damit ein</strong>.',
                'Die Premium-Mitgliedschaft wird deinem Konto zugeordnet. Ab sofort hörst du <strong>werbefrei und offline</strong>.',
              ],
            },
            {
              type: 'hint',
              html: '<b>Beachte:</b> Die Abrechnung läuft eigenständig über die Telekom, unabhängig von einer früheren Zahlart bei Spotify. Hattest du vorher ein direkt bei Spotify bezahltes Premium, beende es zum Laufzeitende, damit nicht beide Abos parallel laufen.',
            },
          ],
        },
        {
          id: 'probleme',
          number: '4',
          heading: 'Häufige Fragen und Probleme',
          blocks: [
            {
              type: 'faq',
              items: [
                {
                  question: 'Ich bekomme keine SMS mit Code.',
                  answer:
                    'Prüfe, ob deine SIM aktiv ist und Empfang hat. Bei Prepaid kann die SMS bis zu zwei Minuten brauchen. Funktioniert es danach noch nicht, fordere den Code direkt erneut über den Link <strong>„Code erneut senden“</strong> an.',
                },
                {
                  question:
                    'Warum kann ich Spotify nicht im Spotify-Checkout per Handyrechnung zahlen?',
                  answer:
                    'Spotify bietet die Telekom Handyrechnung <strong>nicht im eigenen Bezahlvorgang</strong> an. Der offizielle Weg führt über die <strong>Telekom Optionsübersicht</strong>, dort buchst du Premium und die Telekom rechnet ab. Über die Spotify-App auf dem iPhone ist ein Premium-Kauf generell nicht möglich.',
                },
                {
                  question: 'Was passiert, wenn ich Spotify kündigen will?',
                  answer:
                    'Da du Spotify Premium <strong>über die Telekom gebucht</strong> hast, beendest du die Option auch dort: in <strong>MeinMagenta</strong> unter deinen Tarif-Optionen bzw. auf der Spotify-Optionsseite. Dein Spotify-Konto selbst bleibt bestehen, nur die Premium-Abrechnung endet zum nächstmöglichen Termin.',
                },
                {
                  question:
                    'Kann ich mit die Bezahlung per Handyrechnung auch das Spotify-Geschenk-Abo bezahlen?',
                  answer:
                    'Aktuell <strong>nein</strong>. Über die Telekom buchst du nur dein eigenes laufendes Premium-Abo. Geschenkkarten bezahlst du klassisch per Kreditkarte, PayPal oder Spotify-Code.',
                },
                {
                  question: 'Wo sehe ich den Spotify-Posten auf der Telekom Rechnung?',
                  answer:
                    'In <strong>MeinMagenta</strong> unter <strong>„Rechnungen → Drittanbieter-Leistungen“</strong>. Dort ist Spotify Premium mit Datum, Betrag und Abrechnungszeitraum aufgeführt. Auch deine Drittanbieter-Sperre lässt sich dort jederzeit anpassen.',
                },
              ],
            },
          ],
        },
      ],
    },
  },
  {
    id: 'spotify-premium',
    cat: 'streaming',
    card: {
      heading: { ...cardHeading, text: 'Spotify' },
      text: 'Spotify Premium buchen und bei ausgewählten Abos die Abrechnung per Handyrechnung wählen. Millionen Titel werbefrei.',
      buttons: [
        {
          tag: 'a',
          href: 'https://www.spotify.com/de/',
          isExternal: true,
          label: 'Weiter zu Spotify',
          primary: true,
        },
      ],
      image: {
        src: '/content/content-images/dcb/messe/partner/0e56026d9e356b3b3fad7f8e009678eb/logo-partner-spotify-premium.svg',
        lazy: true,
        alt: 'Spotify Premium Logo',
        aspectRatio: '441.7 / 111.9',
      },
    },
    anleitung: {
      eyebrow: 'Anleitung · Spotify',
      title: 'So bezahlst du Spotify ',
      titleAccent: 'per Handyrechnung.',
      lead: 'Spotify Premium direkt über deine Telekom Mobilfunkrechnung bezahlen, ohne Kreditkarte oder PayPal. Funktioniert für neue Accounts und für bestehende Abos.',
      meta: {
        duration: 'Dauer: ca. 4 Minuten',
        device: 'Gerät: Browser oder Smartphone',
        confirmation: 'Sicher per SMS-OTP',
      },
      sections: [
        {
          id: 'start',
          number: '1',
          heading: 'Bevor du startest',
          blocks: [
            {
              type: 'paragraph',
              html: 'Spotify Premium buchst du <strong>direkt bei Spotify</strong>. Im Bezahlvorgang wählst du für ausgewählte Abos die Abrechnung <b>per Handyrechnung</b>, bestätigst per SMS-Code, und der Monatsbetrag läuft ab dann <strong>über deine Telekom Mobilfunkrechnung</strong>. Eine Kreditkarte oder ein PayPal-Login ist dafür nicht nötig.',
            },
            { type: 'subheading', text: 'Du brauchst dazu:' },
            {
              type: 'list',
              items: [
                'Einen aktiven <strong>Telekom Mobilfunkvertrag</strong>.',
                'Dein Telekom-<strong>Smartphone griffbereit</strong>, weil du die Zahlung per SMS-Code bestätigst.',
                'Optional: einen <strong>Spotify-Account</strong>. Wenn du noch keinen hast, legst du ihn im selben Schritt mit an.',
              ],
            },
            {
              type: 'hint',
              html: 'Wichtig: Die Handyrechnung steht bei Spotify nur für <strong>ausgewählte Premium-Abos</strong> zur Verfügung, nicht für Premium Family oder Premium Student. Welche Zahlarten für deinen Wunsch-Plan gelten, siehst du im Bezahlvorgang. Das kann von Kunde zu Kunde unterschiedlich sein.',
            },
          ],
        },
        {
          id: 'buchen',
          number: '2',
          heading: 'Spotify Premium buchen',
          blocks: [
            {
              type: 'paragraph',
              html: 'So buchst du Spotify Premium und zahlst per Handyrechnung:',
            },
            {
              type: 'list',
              items: [
                'Öffne <a href="https://www.spotify.com/de/premium/" target="_blank" rel="noopener" class="link link--external">spotify.com/de/premium</a> und wähle den gewünschten <b>Premium-Plan.</b>',
                'Im Bezahlvorgang wählst du <strong>„Über Mobilfunkanbieter bezahlen“</strong> als Zahlart. Steht die Option nicht zur Auswahl, ist sie für diesen Plan nicht verfügbar.',
                'Gib deine <strong>Telekom Mobilfunknummer</strong> ein.',
                'Du bekommst per SMS einen <strong>Bestätigungscode</strong>. Trage ihn ein, um die Zahlung abzuschließen.',
                'Spotify Premium ist aktiv. Der Monatsbetrag erscheint ab sofort als <strong>eigene Position auf deiner Telekom Mobilfunkrechnung</strong>, kein Sammel-Eintrag.',
              ],
            },
          ],
        },
        {
          id: 'konto',
          number: '3',
          heading: 'Abrechnung über die Telekom Rechnung',
          blocks: [
            {
              type: 'paragraph',
              html: 'Was nach der Buchung passiert:',
            },
            {
              type: 'list',
              items: [
                'Die Premium-Mitgliedschaft liegt auf <strong>deinem Spotify-Konto</strong>. Ab sofort hörst du <strong>werbefrei und offline</strong>.',
                'Der Monatsbetrag wird über deine <strong>Telekom Mobilfunknummer</strong> abgerechnet und erscheint auf der nächsten Rechnung unter <strong>„Telekom · Bezahlen per Handyrechnung“</strong>, mit Anbieter und Datum.',
                'Deine Zahlungsdaten bleiben bei der Telekom: an Spotify werden <strong>keine Konto- oder Kartendaten</strong> übertragen.',
              ],
            },
            {
              type: 'hint',
              html: '<b>Beachte:</b> Ein Spotify-Premium-Abo läuft jeweils nur einmal pro Konto. Hast du bereits ein Premium mit einer anderen Zahlart, stellst du die Zahlart in deinem Spotify-Konto um, statt ein zweites Abo abzuschließen.',
            },
          ],
        },
        {
          id: 'probleme',
          number: '4',
          heading: 'Häufige Fragen und Probleme',
          blocks: [
            {
              type: 'faq',
              items: [
                {
                  question: 'Ich bekomme keine SMS mit Code.',
                  answer:
                    'Prüfe, ob deine SIM aktiv ist und Empfang hat. Bei Prepaid kann die SMS bis zu zwei Minuten brauchen. Funktioniert es danach noch nicht, fordere den Code direkt erneut über den Link <strong>„Code erneut senden“</strong> an.',
                },
                {
                  question: 'Für welche Spotify-Abos geht die Handyrechnung?',
                  answer:
                    'Die Zahlart steht bei Spotify nur für <strong>ausgewählte Premium-Abos</strong> zur Verfügung. <strong>Premium Family und Premium Student sind ausgenommen.</strong> Welche Abos sie anbieten, kann sich zudem von Kunde zu Kunde unterscheiden. Ob dein Wunsch-Plan sie anbietet, siehst du deshalb erst im Bezahlvorgang: erscheint dort „Über Mobilfunkanbieter bezahlen“, kannst du sie wählen. Über die Spotify-App auf dem iPhone ist ein Premium-Kauf generell nicht möglich, nutze dafür den Browser.',
                },
                {
                  question: 'Was passiert, wenn ich Spotify kündigen will?',
                  answer:
                    'Du kündigst <strong>direkt bei Spotify</strong> in deinem Konto, ganz wie gewohnt. Es gibt keine Bindung an die Telekom. Mit dem Ende des Abos endet auch die Abrechnung über deine Mobilfunkrechnung.',
                },
                {
                  question:
                    'Kann ich mit der Bezahlung per Handyrechnung auch ein Spotify-Geschenk-Abo bezahlen?',
                  answer:
                    'Aktuell <strong>nein</strong>. Die Handyrechnung gilt für dein eigenes laufendes Premium-Abo. Geschenkkarten bezahlst du klassisch per Kreditkarte, PayPal oder Spotify-Code.',
                },
                {
                  question: 'Wo sehe ich den Spotify-Posten auf der Telekom Rechnung?',
                  answer:
                    'In <strong>MeinMagenta</strong> unter <strong>„Rechnungen → Drittanbieter-Leistungen“</strong>. Dort ist Spotify Premium mit Datum, Betrag und Abrechnungszeitraum aufgeführt. Auch deine Drittanbietersperre lässt sich dort jederzeit anpassen.',
                },
              ],
            },
          ],
        },
      ],
    },
  },
  {
    cat: 'streaming',
    card: {
      heading: { ...cardHeading, text: 'Deezer' },
      text: 'Deezer Premium in der App kaufen und den In-App-Kauf per Handyrechnung zahlen. Über 120 Millionen Songs.',
      buttons: [
        {
          tag: 'a',
          href: 'https://www.deezer.com/de/',
          isExternal: true,
          label: 'Weiter zu Deezer',
          primary: true,
        },
      ],
      image: {
        src: '/content/content-images/dcb/messe/partner/cd04a0dbe71dc6b66f5976c634c87c13/deezer-logo.svg',
        lazy: true,
        alt: 'Deezer Logo',
        aspectRatio: '173 / 48',
      },
    },
    anleitung: {
      eyebrow: 'Anleitung · Deezer',
      title: 'So bezahlst du Deezer Premium ',
      titleAccent: 'per Handyrechnung.',
      lead: 'Deezer Premium per Handyrechnung über die Telekom Mobilfunkrechnung bezahlen. Der Workaround läuft über den App-Store-Kauf, weil Deezer aktuell keine direkte Carrier-Billing-Option im Web-Checkout anbietet.',
      meta: {
        duration: 'Dauer: ca. 5 Minuten',
        device: 'Gerät: iPhone, iPad oder Android-Smartphone',
        confirmation: 'Sicher über App-Store-Abrechnung',
      },
      sections: [
        {
          id: 'start',
          number: '1',
          heading: 'So funktioniert es bei Deezer',
          blocks: [
            {
              type: 'paragraph',
              html: 'Deezer bietet in Deutschland <u>keine direkte Zahlung per Handyrechnung im eigenen Konto</u>. Auf <strong>deezer.com/account/payment-info</strong> kannst du nur Kreditkarte, PayPal oder Apple/Google Pay hinterlegen. Wer Deezer Premium trotzdem über die Telekom Mobilfunkrechnung zahlen möchte, geht den <strong>Weg über die App Stores</strong>: Du schließt das Abo in der Deezer-App auf Android oder iOS ab und bezahlst dort mit deiner hinterlegten Handyrechnungs-Zahlart.',
            },
            { type: 'subheading', text: 'Du brauchst dazu:' },
            {
              type: 'list',
              items: [
                'Einen aktiven <strong>Telekom Mobilfunkvertrag</strong> mit deaktivierter Drittanbietersperre.',
                'Die Bezahlung per Handyrechnung bereits hinterlegt in deinem App Store. Falls noch nicht: <strong>Google Play einrichten</strong> oder <strong>Apple App Store einrichten</strong>.',
                'Die <strong>Deezer-App</strong> auf deinem Smartphone (nicht den Browser).',
              ],
            },
            {
              type: 'hint',
              html: '<b>Hinweis:</b> Hast du aktuell ein <strong>Web-Abo direkt bei Deezer</strong>, läuft das nach diesem Wechsel <u>nicht automatisch aus</u>. Du musst es separat unter <strong>deezer.com/account/subscription</strong> kündigen, sonst zahlst du parallel.',
            },
          ],
        },
        {
          id: 'android',
          number: '2',
          heading: 'Über Google Play (Android)',
          blocks: [
            {
              type: 'list',
              items: [
                'Öffne die <strong>Deezer-App</strong> auf deinem Android-Smartphone.',
                'Logge dich in dein Deezer-Konto ein oder lege ein neues an.',
                'Tippe auf <strong>„Premium ausprobieren“</strong> oder <strong>„Premium upgraden“</strong>.',
                'Im Bezahlfenster siehst du die in Google Play hinterlegten Zahlarten. Wähle <strong>„Über Mobilfunkanbieter abrechnen“</strong> (Telekom).',
                'Bestätige den Kauf mit Fingerabdruck, PIN oder Google-Passwort. Das Abo läuft ab sofort über Google Play und damit über deine Telekom Mobilfunkrechnung.',
              ],
            },
          ],
        },
        {
          id: 'ios',
          number: '3',
          heading: 'Über Apple App Store (iOS)',
          blocks: [
            {
              type: 'list',
              items: [
                'Öffne die <strong>Deezer-App</strong> auf deinem iPhone oder iPad.',
                'Logge dich in dein Deezer-Konto ein oder erstelle ein neues.',
                'Tippe auf <strong>„Auf Premium upgraden“</strong>.',
                'Im iOS-Bezahlfenster zeigt Apple deine hinterlegten Zahlmethoden. Stelle sicher, dass <strong>Telekom</strong> ganz oben in deiner Liste steht (Einstellungen → Apple-ID → Zahlung & Versand → Bearbeiten).',
                'Bestätige mit Face ID oder Touch ID. Das Abo erscheint anschließend in <strong>Einstellungen → Apple-ID → Abos</strong> mit Telekom als Zahlmethode.',
              ],
            },
          ],
        },
        {
          id: 'probleme',
          number: '4',
          heading: 'Häufige Fragen und Probleme',
          blocks: [
            {
              type: 'faq',
              items: [
                {
                  question: 'Warum geht es nicht direkt auf deezer.com?',
                  answer:
                    'Deezer Deutschland hat aktuell keinen direkten Vertrag mit der Telekom für die Zahlung per Handyrechnung. Telekom Österreich hat ein eigenes Deezer-Bundle, Telekom Deutschland nicht. Der Weg über die App Stores ist der einzige offizielle.',
                },
                {
                  question: 'Mein Deezer-Family-Abo lässt sich nicht umstellen.',
                  answer:
                    'Family-Pläne können <strong>nur vom Family-Owner</strong> geändert werden. Logge dich mit dem Hauptkonto in der Deezer-App ein, dort siehst du den Family-Plan und kannst ihn auf die App-Store-Abrechnung umstellen.',
                },
                {
                  question: 'StreamOn Music — ist das auch die Bezahlung per Handyrechnung?',
                  answer:
                    'Nein. <strong>StreamOn Music</strong> ist ein Datenvolumen-Privileg deines Telekom Tarifs (Streaming zählt nicht aufs Datenkontingent), <u>nicht</u> die Bezahlung. Du brauchst trotzdem ein laufendes Deezer-Abo, das wahlweise per Karte, PayPal oder eben die Bezahlung per Handyrechnung über die Stores bezahlt wird.',
                },
                {
                  question: 'Wo sehe ich den Posten auf der Telekom Rechnung?',
                  answer:
                    'In <strong>MeinMagenta → Rechnungen → Drittanbieter-Leistungen</strong>. Bei Android steht dort <em>„Google Commerce Limited“</em> (mit Deezer Premium als Verwendungszweck), bei iOS <em>„Apple Distribution International“</em>. Eine direkte „Deezer SA“-Position erscheint nicht.',
                },
                {
                  question: 'Was passiert mit meinem aktuellen Web-Abo?',
                  answer:
                    'Es läuft <strong>nicht automatisch aus</strong>. Logge dich auf <strong>deezer.com/account/subscription</strong> ein und kündige das alte Abo, sonst zahlst du parallel sowohl Web-Abo als auch Store-Abo.',
                },
              ],
            },
          ],
        },
      ],
    },
  },
  {
    cat: 'apps',
    id: 'apple-appstore',
    card: {
      heading: { ...cardHeading, text: 'Apple App Store' },
      text: 'Apps, Spiele und Abos für iPhone und iPad. Handyrechnung als Bezahlart hinterlegen und bis zu 9,99 € Cashback sichern.',
      buttons: [
        {
          tag: 'a',
          href: 'https://www.apple.com/de/app-store/',
          isExternal: true,
          label: 'Weiter zum App-Store',
          primary: true,
        },
      ],
      image: {
        src: '/content/content-images/dcb/messe/partner/54becf0ade1d949e0b42df8bd4f78381/app-store-icon.svg',
        lazy: true,
        alt: 'Apple App Store Logo',
        aspectRatio: '1',
      },
    },
    anleitung: {
      eyebrow: 'Anleitung · Apple App Store',
      title: 'So richtest du die Bezahlung per Handyrechnung ',
      titleAccent: 'im Apple App Store ein.',
      lead: 'App-Käufe, In-App-Abos und Spielwährungen ab sofort direkt über deine Telekom Mobilfunkrechnung bezahlen, ohne Kreditkarte. In wenigen Minuten erledigt, egal ob du eine neue Apple-ID anlegst oder dein bestehendes Konto umstellst.',
      meta: {
        duration: 'Dauer: ca. 5 Minuten',
        device: 'Gerät: iPhone oder iPad',
        confirmation: 'Sicher per SMS-OTP',
      },
      sections: [
        {
          id: 'start',
          number: '1',
          heading: 'Bevor du startest',
          blocks: [
            {
              type: 'paragraph',
              html: 'Per Handyrechnung zahlst du Apps und Abos im Apple App Store <strong>direkt über deine Telekom Mobilfunkrechnung</strong>. Du brauchst keine Kreditkarte, keine PayPal-Verknüpfung und kein zusätzliches Konto. Die Beträge erscheinen monatlich als <strong>eigene Position</strong> auf deiner Telekom Rechnung, mit Datum und Anbieter.',
            },
            { type: 'subheading', text: 'Du brauchst dazu:' },
            {
              type: 'list',
              items: [
                'Einen aktiven <strong>Telekom Mobilfunkvertrag</strong> (Postpaid oder Prepaid). Bei Prepaid erfolgt die Abrechnung über dein <strong>Prepaid-Guthaben</strong> statt über die Monatsrechnung. Sonst identisch.',
                'Dein <strong>iPhone oder iPad mit iOS 17 oder neuer</strong> und eingelegter Telekom SIM oder eSIM.',
                'Eine <strong>Apple-ID</strong>. Wenn du noch keine hast, legst du sie in <strong>Schritt 2</strong> direkt mit an.',
              ],
            },
            {
              type: 'hint',
              html: 'Wichtig: Die Bezahlung läuft <b>nur über die Mobilfunknummer, mit der du den Telekom Vertrag abgeschlossen hast</b>. Eine Festnetznummer geht nicht. Die Drittanbietersperre („Drittanbieter-Leistungen“) muss in deiner MeinMagenta-App auf <u>„zugelassen“</u> stehen, damit App-Store-Beträge abgebucht werden dürfen.',
            },
          ],
        },
        {
          id: 'neu',
          number: '2',
          heading: 'Du legst eine neue Apple-ID an',
          blocks: [
            {
              type: 'paragraph',
              html: 'So aktivierst du die Bezahlung per Handyrechnung parallel zum Anlegen einer Apple-ID, ohne Kreditkartendaten zu hinterlegen:',
            },
            {
              type: 'list',
              items: [
                'Öffne die <strong>Einstellungen-App</strong> auf deinem iPhone und tippe ganz oben auf <strong>Bei deinem iPhone anmelden</strong>.',
                'Wähle <strong>Apple-ID nicht vorhanden oder vergessen?</strong> und dann <strong>Apple-ID erstellen</strong>.',
                'Trage Vorname, Nachname, Geburtsdatum, eine <strong>gültige E-Mail-Adresse</strong> und ein Passwort ein. Bestätige die E-Mail über den per Mail verschickten Code.',
                'Im nächsten Schritt fragt iOS nach einer Zahlungsmethode. <strong>Wähle hier nicht Kreditkarte und nicht PayPal</strong>, sondern scrolle zu <strong>„Mobilfunkanbieter“</strong> oder <strong>„Über Mobilfunkanbieter abrechnen“</strong>.',
                'iOS erkennt deine SIM und schlägt <strong>Telekom Deutschland</strong> vor. Bestätige die Auswahl, gib deine Telekom Mobilfunknummer ein und tippe auf <strong>Weiter</strong>.',
                'iOS schließt die Apple-ID-Anlage ab und meldet dich automatisch im App Store an, fertig.',
              ],
            },
            {
              type: 'paragraph',
              html: 'Ab jetzt landen alle App-Store-Käufe (Apps, In-App-Items, iCloud-Abos, Apple Arcade etc.) <strong>automatisch auf deiner Telekom Mobilfunkrechnung</strong>, eindeutig pro Posten ausgewiesen.',
            },
          ],
        },
        {
          id: 'bestand',
          number: '3',
          heading: 'Du hast bereits eine Apple-ID',
          blocks: [
            {
              type: 'paragraph',
              html: 'Bisher hast du den App Store mit Kreditkarte, PayPal oder Apple-Guthaben bezahlt? So stellst du auf die Bezahlung per Handyrechnung um, ohne deinen App-Bestand zu verlieren:',
            },
            {
              type: 'list',
              items: [
                'Öffne <strong><a href="https://apple.co/Sign_Up_DE_Social" target="_blank" class="link">Einstellungen</a> → [Dein Name]</strong> ganz oben.',
                'Tippe auf <strong>Zahlung & Versand</strong>. Wenn du jetzt Face ID, Touch ID oder dein Apple-ID-Passwort bestätigen sollst, mach das.',
                'Tippe unter den vorhandenen Zahlungsarten auf <strong>Zahlungsmethode hinzufügen</strong>.',
                'Scrolle in der Liste nach unten und wähle <strong>„Mobilfunkanbieter“</strong> (auf manchen iOS-Versionen heißt es <strong>„Über Mobilfunkanbieter abrechnen“</strong>).',
                'iOS erkennt deine Telekom SIM und füllt deine Mobilfunknummer automatisch ein. Falls nicht, gib sie manuell ein. Tippe auf <strong>Fertig</strong>.',
                'Telekom erscheint nun in deiner Zahlungs-Liste. Tippe auf <strong>Bearbeiten</strong> und ziehe Telekom an die <strong>oberste Position</strong>, damit alle künftigen Käufe darüber laufen.',
              ],
            },
            {
              type: 'hint',
              html: '<b>Tipp:</b> Du kannst Kreditkarte und PayPal als Backup im Konto behalten. iOS nutzt Telekom dann zuerst, und greift nur dann auf die nächsten Methoden zurück, wenn das Mobilfunk-Limit für den Monat überschritten ist. <u>Aktive Abos werden bei der nächsten Verlängerung automatisch auf Telekom umgestellt</u>, du musst nichts kündigen.',
            },
          ],
        },
        {
          id: 'in-app',
          number: '4',
          heading: 'In-App-Käufe und laufende Abos',
          blocks: [
            {
              type: 'paragraph',
              html: 'Die Bezahlung per Handyrechnung funktioniert genauso für In-App-Käufe (z. B. zusätzliche Münzen in Mobile-Games) und für alle App-Store-Abonnements wie Apple Music, Apple TV+, Apple Arcade oder Drittanbieter-Abos.',
            },
            { type: 'subheading', text: 'Limits pro Monat' },
            {
              type: 'paragraph',
              html: 'Für den Apple App Store ist der maximale Monatsumsatz über die Handyrechnung aktuell auf <strong>50 € pro Telekom Vertrag</strong> begrenzt. Erreichst du das Limit, zieht iOS die nächste hinterlegte Zahlungsart, sofern vorhanden. In MeinMagenta kannst du dein persönliches Limit zusätzlich <u>niedriger setzen</u>, z. B. für Kinder-Geräte.',
            },
            { type: 'subheading', text: 'Erstattung & Stornos' },
            {
              type: 'paragraph',
              html: 'Apple wickelt Erstattungen wie gewohnt direkt im Account ab (<a href="https://reportaproblem.apple.com/" target="_blank" rel="noopener" class="link link--external">reportaproblem.apple.com</a>). Genehmigte Erstattungen schreibt die Telekom dir mit der nächsten Mobilfunkrechnung gut, du brauchst dich nicht separat bei der Telekom melden.',
            },
          ],
        },
        {
          id: 'probleme',
          number: '5',
          heading: 'Häufige Fragen und Probleme',
          blocks: [
            {
              type: 'faq',
              items: [
                {
                  question: 'iOS zeigt mir „Mobilfunkanbieter“ nicht als Zahlungsmethode an.',
                  answer:
                    'Das passiert meist, wenn die <strong>Telekom SIM nicht aktiv im Gerät</strong> ist oder Drittanbieter-Leistungen in MeinMagenta gesperrt sind. Prüfe in <strong>Einstellungen → Mobilfunk</strong>, ob die Telekom SIM eingelegt und aktiviert ist, und schau in der MeinMagenta-App unter <strong>Mein Vertrag → Drittanbietersperre</strong>, ob die Sperre für <u>„digitale Dienste“</u> aufgehoben ist.',
                },
                {
                  question:
                    'Ich nutze Apple Familienfreigabe. Kann jedes Familienmitglied per Telekom zahlen?',
                  answer:
                    'Nein. Bei Familienfreigabe wird <strong>alle Bezahlung über das Konto des Organisators</strong> abgewickelt. Wenn der Organisator die Handyrechnung hinterlegt, zahlen alle Familienmitglieder automatisch darüber. Möchten Familienmitglieder eigene Zahlungen, müssen sie aus der Familie ausgegliedert werden oder einen eigenen App-Store-Käufer-Status bekommen.',
                },
                {
                  question: 'Wo sehe ich den App-Store-Posten auf der Telekom Rechnung?',
                  answer:
                    'In <strong>MeinMagenta → Rechnungen → Drittanbieter-Leistungen</strong>. Jeder Posten ist mit Datum, Anbieter und Betrag aufgeführt. Bei Apple steht in der Regel <em>„Apple Distribution International, App Store“</em> als Anbieter.',
                },
                {
                  question:
                    'Kann ich die Bezahlung per Handyrechnung wieder aus dem App Store entfernen?',
                  answer:
                    'Ja, jederzeit. Geh in <strong>Einstellungen → [Dein Name] → Zahlung & Versand</strong>, wische die Telekom Zeile nach links und tippe auf <strong>Löschen</strong>. Eventuell laufende Abos werden danach mit der nächsten verfügbaren Zahlart abgerechnet.',
                },
              ],
            },
          ],
        },
      ],
    },
  },
  {
    cat: 'apps',
    id: 'google-playstore',
    card: {
      heading: { ...cardHeading, text: 'Google Play Store' },
      text: 'Millionen Android-Apps, Spiele und In-App-Käufe. Handyrechnung als Bezahlart hinterlegen.',
      buttons: [
        {
          tag: 'a',
          href: 'https://play.google.com/store/games',
          isExternal: true,
          label: 'Weiter zu Google Play',
          primary: true,
        },
      ],
      image: {
        src: '/content/content-images/dcb/messe/partner/3eb13bd4cbef9709059c9a6f639ca3a0/google-play.svg',
        lazy: true,
        alt: 'Google Play Logo',
        aspectRatio: '29 / 32',
      },
    },
    anleitung: {
      eyebrow: 'Anleitung · Google Play Store',
      title: 'So richtest du die Bezahlung per Handyrechnung ',
      titleAccent: 'im Google Play Store ein.',
      lead: 'Apps, In-App-Käufe und Abos im Google Play Store direkt über deine Telekom Mobilfunkrechnung bezahlen, ohne Kreditkarte und ohne Bankkonto. Funktioniert nur in der Play-Store-App, nicht auf play.google.com.',
      meta: {
        duration: 'Dauer: ca. 5 Minuten',
        device: 'Gerät: Android-Smartphone',
        confirmation: 'Sicher per SMS-TAN',
      },
      sections: [
        {
          id: 'start',
          number: '1',
          heading: 'Bevor du startest',
          blocks: [
            {
              type: 'paragraph',
              html: 'Per Handyrechnung zahlst du Apps und Abos im Google Play Store <strong>direkt über deine Telekom Mobilfunkrechnung</strong>. Du brauchst keine Kreditkarte, kein PayPal, kein Bankkonto. Die Beträge erscheinen als <strong>Drittanbieter-Leistung</strong> auf deiner Telekom Rechnung.',
            },
            { type: 'subheading', text: 'Du brauchst dazu:' },
            {
              type: 'list',
              items: [
                'Einen aktiven <strong>Telekom Mobilfunkvertrag</strong> (Postpaid oder Prepaid, bei Prepaid läuft die Abrechnung über das Prepaid-Guthaben). Reseller wie congstar, freenet oder mobilcom-debitel werden in der Regel <u>nicht unterstützt</u>.',
                'Dein <strong>Android-Smartphone mit der Telekom SIM in Slot 1</strong>. Bei Dual-SIM-Geräten sollte Slot 2 leer bleiben, sonst erkennt Google den Carrier nicht.',
                'Die <strong>Google-Play-App</strong> (kein Webbrowser). Carrier Billing ist auf play.google.com nicht verfügbar.',
              ],
            },
            {
              type: 'hint',
              html: 'Wichtig: Prüf vorher in <strong>MeinMagenta → Mein Vertrag → Drittanbietersperre</strong>, ob „digitale Dienste“ freigeschaltet sind. Die Drittanbietersperre ist der häufigste Grund, warum die SMS-TAN nie ankommt. Auf <u>gerooteten Geräten</u> funktioniert die Bezahlung per Handyrechnung zudem nicht.',
            },
          ],
        },
        {
          id: 'neu',
          number: '2',
          heading: 'Du legst ein neues Google-Konto an',
          blocks: [
            {
              type: 'paragraph',
              html: 'Google koppelt Account-Anlage und Bezahl-Setup nicht. Du legst erst dein Google-Konto an und ergänzt anschließend die Handyrechnung als Zahlart:',
            },
            {
              type: 'list',
              items: [
                'Öffne <strong>Einstellungen → Konten → Konto hinzufügen → Google</strong> und folge dem Assistenten. Du wirst nach Vorname, Nachname, Geburtsdatum und einer Wunsch-E-Mail-Adresse gefragt.',
                'Bestätige die E-Mail per Code. Bei der Frage nach einer Zahlungsmethode wählst du <strong>„Später hinzufügen“</strong>, damit du die Bezahlung per Handyrechnung nicht überspringst.',
                'Sobald der Account angelegt ist, fahre mit <strong>Schritt 3</strong> fort, um die Handyrechnung zu hinterlegen.',
              ],
            },
          ],
        },
        {
          id: 'bestand',
          number: '3',
          heading: 'Du hast bereits ein Google-Konto',
          blocks: [
            {
              type: 'paragraph',
              html: 'So fügst du die Bezahlung per Handyrechnung als zusätzliche oder primäre Zahlart hinzu:',
            },
            {
              type: 'list',
              items: [
                'Öffne die <strong>Google-Play-App</strong> auf deinem Android-Phone.',
                'Tippe oben rechts auf dein <strong>Profilbild</strong>.',
                'Wähle <strong>„Zahlungen und Abos“</strong> und dann <strong><a href="https://play.app.goo.gl/x1fX" class="link" target="_blank">„Zahlungsmethoden“</a></strong>.',
                'Die Rubrik <strong>„Zahlungsmethode hinzufügen“</strong> steht bereits oben, du musst nicht nach unten scrollen. Tippe direkt darauf.',
                'Im nächsten Bildschirm erscheint die Option <strong>„Telekom - Zahl einfach per Handyrechnung“</strong>. Wähle sie aus.',
                'Du bekommst zur Bestätigung einen <strong>SMS-Code</strong>. Trage ihn ein, fertig. Die Bezahlung per Handyrechnung erscheint nun in deiner Zahlungsmethoden-Liste.',
              ],
            },
            {
              type: 'hint',
              html: '<b>Tipp:</b> Du kannst andere Zahlarten (Kreditkarte, PayPal, Geschenkkarten) parallel behalten. Beim Kauf wählst du jedes Mal aktiv aus, womit du zahlst. Aktive Abos werden bei der nächsten Verlängerung automatisch über die neue Zahlart abgerechnet, wenn du die Bezahlung per Handyrechnung als Standard setzt.',
            },
          ],
        },
        {
          id: 'kauf',
          number: '4',
          heading: 'Beim Kauf per Handyrechnung zahlen',
          blocks: [
            {
              type: 'paragraph',
              html: 'Anders als Apple speichert Google Play die Bezahlung per Handyrechnung nicht als Default. Du wählst sie pro Kauf aktiv:',
            },
            {
              type: 'list',
              items: [
                'Tippe auf die <strong>App</strong>, das <strong>In-App-Item</strong> oder das <strong>Abo</strong>, das du kaufen willst.',
                'Im Bezahl-Dialog tippst du auf den <strong>Pfeil neben der aktuell angezeigten Zahlart</strong>.',
                'Wähle <strong>„Über Mobilfunkanbieter abrechnen“</strong> (Telekom) aus der Liste.',
                'Bestätige den Kauf mit deiner gewohnten Methode (Fingerabdruck, PIN, Google-Passwort).',
                'Der Betrag erscheint am Monatsende auf deiner Telekom Rechnung.',
              ],
            },
            { type: 'subheading', text: 'Monatslimit für Google Play' },
            {
              type: 'paragraph',
              html: 'Google gibt für Carrier Billing kein hartes Limit vor, sondern verweist auf das Limit der Telekom. In der Praxis sind <strong>typischerweise 30 € pro Monat</strong> möglich. Wer mehr kaufen möchte, kann das Limit über den Telekom Kundenservice anheben lassen.',
            },
          ],
        },
        {
          id: 'probleme',
          number: '5',
          heading: 'Häufige Fragen und Probleme',
          blocks: [
            {
              type: 'faq',
              items: [
                {
                  question: '„Telekom - Zahl einfach per Handyrechnung“ erscheint nicht.',
                  answer:
                    'Stelle sicher, dass die Telekom SIM aktiv und in <strong>Slot 1</strong> liegt. Bei Dual-SIM-Geräten muss Slot 2 leer sein. Außerdem darf die <strong>Drittanbietersperre</strong> in MeinMagenta nicht aktiv sein. Wenn du ein <strong>Reseller-Tarif</strong> (congstar, freenet, mobilcom-debitel, 1&1 im Telekom Netz) hast, ist die Bezahlung per Handyrechnung für Google Play in der Regel nicht freigeschaltet.',
                },
                {
                  question: 'Ich bekomme keine SMS-TAN.',
                  answer:
                    'Warte 60 Sekunden, dann tippe in der Play-App auf <strong>„Code erneut senden“</strong>. Funktioniert das nicht, ist meist die <strong>Drittanbietersperre</strong> aktiv. Schalte sie in MeinMagenta für „digitale Dienste“ frei und versuche es erneut.',
                },
                {
                  question: 'Abos akzeptieren die Bezahlung per Handyrechnung nicht.',
                  answer:
                    'Einige Anbieter erlauben keine Carrier-Billing-Zahlung für ihre Abos (z. B. einige Premium-Abos). In dem Fall siehst du die Bezahlung per Handyrechnung nicht als Option im Bezahl-Dialog. Lösung: Einmal-Käufe und In-App-Items gehen üblicherweise problemlos.',
                },
                {
                  question: 'Wie sehe ich den Posten auf der Telekom Rechnung?',
                  answer:
                    'In <strong>MeinMagenta → Rechnungen → Drittanbieter-Leistungen</strong>. Der Anbieter heißt dort in der Regel <em>„Google Commerce Limited“</em> mit Datum und Betrag. Den genauen Wortlaut zeigt deine erste echte Rechnung.',
                },
                {
                  question: 'Kann ich die Bezahlung per Handyrechnung wieder entfernen?',
                  answer:
                    'Ja, jederzeit. In der Play-App: <strong>Profil → Zahlungen und Abos → Zahlungsmethoden → Telekom → Entfernen</strong>. Laufende Abos werden danach mit der nächsten verfügbaren Zahlart abgerechnet.',
                },
              ],
            },
          ],
        },
      ],
    },
  },
  {
    cat: 'apps',
    card: {
      heading: { ...cardHeading, text: 'Microsoft & Xbox' },
      text: 'Apps, PC- und Xbox-Spiele, Filme und Abos. Bezahlung per Mobilfunkanbieter im Konto hinzufügen.',
      buttons: [
        {
          tag: 'a',
          href: 'https://apps.microsoft.com/home?hl=de-DE&gl=DE',
          isExternal: true,
          label: 'Weiter zu Microsoft Store',
          primary: true,
        },
      ],
      image: {
        src: '/content/content-images/dcb/messe/partner/22853014d8f873339a5a32aa7ce2620b/microsoft-store-xbox-logo.svg',
        lazy: true,
        alt: 'Microsoft & Xbox Store Logo',
        aspectRatio: '563 / 248',
      },
    },
    anleitung: {
      eyebrow: 'Anleitung · Microsoft Store',
      title: 'So richtest du die Bezahlung per Handyrechnung ',
      titleAccent: 'im Microsoft Store ein.',
      lead: 'Microsoft 365, Xbox Game Pass und Apps aus dem Microsoft Store direkt über deine Telekom Mobilfunkrechnung bezahlen. Funktioniert für Abos auf account.microsoft.com und im Windows-Store-Checkout.',
      meta: {
        duration: 'Dauer: ca. 5 Minuten',
        device: 'Gerät: Web oder Windows 10/11',
        confirmation: 'Sicher per SMS-Code',
      },
      sections: [
        {
          id: 'start',
          number: '1',
          heading: 'Bevor du startest',
          blocks: [
            {
              type: 'paragraph',
              html: 'Per Handyrechnung bezahlst du im Microsoft Store <strong>laufende Abos</strong> direkt über deine Telekom Mobilfunkrechnung. Microsoft listet die Methode offiziell als „Mobiltelefon-Abrechnung“. Einmal hinterlegt, wird sie sowohl auf <strong>account.microsoft.com</strong> als auch in der Microsoft-Store-App auf Windows 10/11 genutzt.',
            },
            { type: 'subheading', text: 'Du brauchst dazu:' },
            {
              type: 'list',
              items: [
                'Einen aktiven <strong>Telekom Mobilfunkvertrag</strong>. Die Telefonnummer muss zum <strong>Land deines Microsoft-Kontos</strong> passen (DE-Konto + DE-Nummer).',
                'Ein <strong>Microsoft-Konto</strong>. Wenn du noch keines hast, leg es zuerst auf <a href="https://signup.live.com/" target="_blank" rel="noopener" class="link link--external">signup.live.com</a> an.',
                'Einen Browser auf Smartphone, Tablet oder Laptop, um auf <strong>account.microsoft.com</strong> zuzugreifen.',
              ],
            },
            {
              type: 'hint',
              html: 'Wichtig: Die Bezahlung per Handyrechnung funktioniert beim Microsoft Store <strong>nur für Abos mit monatlicher Abrechnung</strong> (z. B. Microsoft 365 Single monatlich, Xbox Game Pass). <u>Einmalige Käufe wie Xbox-Spiele oder Microsoft 365 Jahresabos akzeptieren in der Regel keine Mobilfunk-Abrechnung</u>. Dafür brauchst du weiterhin Kreditkarte oder PayPal.',
            },
          ],
        },
        {
          id: 'web',
          number: '2',
          heading: 'Die Bezahlung per Handyrechnung über die Webseite hinterlegen',
          blocks: [
            {
              type: 'paragraph',
              html: 'Der zuverlässigste Weg, die Bezahlung per Handyrechnung einmalig zu aktivieren, läuft über das Microsoft-Konto im Browser:',
            },
            {
              type: 'list',
              items: [
                'Öffne <a href="https://account.microsoft.com/" target="_blank" rel="noopener" class="link link--external"><strong>account.microsoft.com</strong></a> und melde dich mit deinem Microsoft-Konto an.',
                'Klicke oben im Menü auf <strong>„Zahlungen und Abrechnung“</strong> und dann auf <strong>„Zahlungsoptionen“</strong>.',
                'Tippe auf <strong>„Neue Zahlungsmethode hinzufügen“</strong>.',
                'Wähle in der Liste <strong>„Mobiltelefon“</strong>.',
                'Bei der Frage nach dem Mobilfunkanbieter wählst du <strong>„Deutsche Telekom AG“</strong>.',
                'Trage deine <strong>Telekom Mobilfunknummer</strong> ein und tippe auf Weiter.',
                'Du erhältst per SMS einen <strong>Bestätigungscode</strong>. Trage ihn ein und klicke auf Bestätigen.',
                'Telekom erscheint nun in deiner Zahlungsoptionen-Liste. Bei laufenden Microsoft-365-Abos kannst du sie unter Abrechnung → Abos → Zahlungsweise ändern auf Telekom umstellen.',
              ],
            },
          ],
        },
        {
          id: 'windows',
          number: '3',
          heading: 'In der Windows-Store-App nutzen',
          blocks: [
            {
              type: 'paragraph',
              html: 'Wenn du die Bezahlung per Handyrechnung schon einmal über die Webseite eingerichtet hast, erscheint sie in der Store-App automatisch im Checkout:',
            },
            {
              type: 'list',
              items: [
                'Öffne den <strong>Microsoft Store</strong> auf deinem Windows 10/11-Gerät.',
                'Wähle das Produkt (z. B. Microsoft 365 Single, Xbox Game Pass Ultimate) und klicke auf <strong>„Kaufen“</strong> oder <strong>„Abonnieren“</strong>.',
                'Im Bezahlfenster klickst du auf <strong>„Zahlungsmethode ändern“</strong>.',
                'Wähle <strong>„Mobiltelefon“</strong> → <strong>„Deutsche Telekom AG“</strong>.',
                'Bestätige den Kauf. Bei der ersten Nutzung kommt eine zusätzliche SMS zur Authentifizierung.',
              ],
            },
            {
              type: 'paragraph',
              html: 'Möchtest du die Bezahlung per Handyrechnung ohne den Umweg über das Web direkt im Store einrichten, geht das auch: Bei der ersten Bestellung wählst du im Bezahlfenster <strong>„Neue Zahlungsmethode hinzufügen“</strong> → <strong>„Mobiltelefon“</strong> und folgst denselben Schritten wie unter <strong>Schritt 2</strong>.',
            },
          ],
        },
        {
          id: 'was',
          number: '4',
          heading: 'Was funktioniert (und was nicht)',
          blocks: [
            { type: 'subheading', text: 'Funktioniert mit die Bezahlung per Handyrechnung:' },
            {
              type: 'list',
              items: [
                '<strong>Microsoft 365 Single</strong> (monatliches Abo)',
                '<strong>Microsoft 365 Family</strong> (monatliches Abo)',
                '<strong>Xbox Game Pass Core / Standard / Ultimate</strong> (monatlich)',
                '<strong>OneDrive-Speicher-Erweiterungen</strong> (monatlich)',
                'Einzelne <strong>Windows-Apps mit Abo-Modell</strong>',
              ],
            },
            { type: 'subheading', text: 'Funktioniert in der Regel nicht:' },
            {
              type: 'list',
              items: [
                'Einzelne <strong>Xbox-Spielkäufe</strong> (One-Shot)',
                '<strong>Microsoft 365 Jahresabos</strong> (Einmalzahlung)',
                '<strong>Bundles und Vorbestellungen</strong> auf der Xbox',
                'Käufe innerhalb von <strong>Drittanbieter-Spielen</strong> über den Store',
              ],
            },
            {
              type: 'paragraph',
              html: 'Microsoft schließt das explizit auf der eigenen Mobile-Billing-Seite aus. Für diese Käufe brauchst du weiterhin Kreditkarte oder PayPal als Alternative im Konto.',
            },
          ],
        },
        {
          id: 'probleme',
          number: '5',
          heading: 'Häufige Fragen und Probleme',
          blocks: [
            {
              type: 'faq',
              items: [
                {
                  question:
                    '„Mobiltelefon“ oder „Deutsche Telekom AG“ erscheint nicht in der Liste.',
                  answer:
                    'Stelle sicher, dass die Region deines Microsoft-Kontos auf <strong>Deutschland</strong> steht. Microsoft koppelt Carrier Billing eng an die Konto-Region. Prüf das unter account.microsoft.com → Profil → Land/Region.',
                },
                {
                  question: 'Ich bekomme keinen SMS-Code.',
                  answer:
                    'Das passiert bei <strong>Business-Verträgen mit MultiSIM</strong> häufig: Microsoft schickt die SMS an die Haupt-SIM, nicht an die genutzte Datensim. Lege die Haupt-Telekom SIM kurz ins Gerät, bestätige den Code, danach kannst du auf die andere SIM wechseln. Auch hier gilt: <strong>Drittanbietersperre</strong> in MeinMagenta darf nicht aktiv sein.',
                },
                {
                  question: 'Ich möchte mein Microsoft-365-Jahresabo per Telekom zahlen, geht das?',
                  answer:
                    'Nein. Jahresabos werden als <strong>Einmalzahlung</strong> abgewickelt und Microsoft erlaubt dafür keine Carrier-Billing-Methode. Workaround: Auf monatliche Abrechnung umstellen — dann läuft die Zahlung über die Handyrechnung weiter.',
                },
                {
                  question: 'Wie sehe ich den Posten auf der Telekom Rechnung?',
                  answer:
                    'In <strong>MeinMagenta → Rechnungen → Drittanbieter-Leistungen</strong>. Der Anbieter heißt dort in der Regel <em>„Microsoft Ireland Operations Ltd.“</em> mit dem Abrechnungsmonat und dem Betrag.',
                },
                {
                  question: 'Was passiert, wenn ich meine Mobilfunknummer wechsle?',
                  answer:
                    'Die hinterlegte Zahlungsmethode wird <strong>nicht automatisch übernommen</strong>. Du musst die Bezahlung per Handyrechnung nach dem Wechsel einmalig neu hinterlegen (siehe <strong>Schritt 2</strong>) und in deinen Abos neu auswählen.',
                },
              ],
            },
          ],
        },
      ],
    },
  },
  {
    cat: 'apps',
    card: {
      heading: { ...cardHeading, text: 'PlayStation Store' },
      text: 'Spiele, Add-ons und Guthaben für PS5 und PS4. Einfach per Handyrechnung beim Checkout zahlen.',
      buttons: [
        {
          tag: 'a',
          href: 'https://store.playstation.com/de-de/pages/latest',
          isExternal: true,
          label: 'Weiter zu PlayStation Store',
          primary: true,
        },
      ],
      image: {
        src: '/content/content-images/dcb/messe/partner/98da503852eee51f14e942448420ad0f/playstation-store-logo.svg',
        lazy: true,
        alt: 'PlayStation Store Logo',
        aspectRatio: '99 / 123',
      },
    },
    anleitung: {
      eyebrow: 'Anleitung · PlayStation Store',
      title: 'So richtest du die Bezahlung per Handyrechnung ',
      titleAccent: 'im PlayStation Store ein.',
      lead: 'Spiele, Add-ons, PlayStation Plus und Wallet-Guthaben direkt über deine Telekom Mobilfunkrechnung bezahlen. Anders als bei anderen Stores bestätigst du jeden Kauf einzeln per SMS-Code, eine dauerhafte Default-Zahlart speichert PSN nicht.',
      meta: {
        duration: 'Dauer: ca. 4 Minuten',
        device: 'Gerät: PS5, PS4 oder Web',
        confirmation: 'Sicher per SMS-TAN pro Kauf',
      },
      sections: [
        {
          id: 'start',
          number: '1',
          heading: 'Bevor du startest',
          blocks: [
            {
              type: 'paragraph',
              html: 'Im PlayStation Store funktioniert die Bezahlung per Handyrechnung in DE meist als <strong>Wallet-Aufladung</strong>: Du lädst dein PSN-Guthaben per Handyrechnung auf und kaufst dann aus dem Wallet. Eine dauerhaft gespeicherte Default-Zahlart wie bei Apple gibt es <u>nicht</u>, jede Aufladung bestätigst du einzeln per SMS-TAN.',
            },
            { type: 'subheading', text: 'Du brauchst dazu:' },
            {
              type: 'list',
              items: [
                'Einen aktiven <strong>Telekom Mobilfunkvertrag</strong>. Die <strong>PSN-Region</strong> deines Accounts muss ebenfalls <strong>Deutschland</strong> sein, sonst greift die Bezahlung per Handyrechnung nicht.',
                'Ein <strong>PSN-Konto (Sony-Konto)</strong> ab 18 Jahren. Sub-Accounts (Kinderkonten unter 18) können selbst nicht zahlen, hier läuft die Abrechnung über das Erwachsenen-Hauptkonto.',
                'Deine <strong>PS5 oder PS4</strong>, alternativ einen Browser für <a href="https://store.playstation.com/de-de/" target="_blank" rel="noopener" class="link link--external">store.playstation.com</a>.',
              ],
            },
            {
              type: 'hint',
              html: 'Wichtig: Die Bezahlung per Handyrechnung zahlt im PlayStation Store typischerweise <strong>bis 50 € pro Monat</strong> pro Telekom Vertrag. Drittanbietersperre in MeinMagenta muss freigegeben sein. <u>Promo-Codes und einzelne Vorbestellungen akzeptieren teils nur Karte oder Wallet</u> — die normale Wallet-Aufladung über die Handyrechnung umgeht das.',
            },
          ],
        },
        {
          id: 'ps5',
          number: '2',
          heading: 'Auf der PS5 Guthaben aufladen',
          blocks: [
            { type: 'paragraph', html: 'Der zuverlässigste Weg auf der PS5:' },
            {
              type: 'list',
              items: [
                'Drücke die PS-Taste auf dem Controller, gehe zu deinem <strong>Profil-Icon</strong> oben rechts und wähle <strong>„Einstellungen“</strong>.',
                'Wähle <strong>„Benutzer und Konten“ → „Konto“ → „Zahlung und Abonnements“</strong>.',
                'Wähle <strong>„Guthaben hinzufügen“</strong>.',
                'Gib den Betrag ein, den du aufladen willst (5 €, 10 €, 25 €, 50 € oder ein eigener Betrag).',
                'Im Zahlungs-Auswahlbildschirm wählst du <strong>„Per Handyrechnung zahlen“</strong> (englisch: „Pay By Mobile“).',
                'Gib deine <strong>Telekom Mobilfunknummer</strong> ein und bestätige den per SMS verschickten <strong>Sicherheitscode</strong>.',
                'Das Guthaben wird sofort auf deinem PSN-Wallet gutgeschrieben. Mit ihm bezahlst du anschließend Spiele, Add-ons, PlayStation Plus.',
              ],
            },
            {
              type: 'paragraph',
              html: 'Alternativ kannst du auch direkt im Store-Checkout eines Spiels <strong>„Per Handyrechnung zahlen“</strong> wählen, dann läuft die Aufladung im Hintergrund automatisch.',
            },
          ],
        },
        {
          id: 'ps4',
          number: '3',
          heading: 'Auf der PS4 Guthaben aufladen',
          blocks: [
            {
              type: 'paragraph',
              html: 'Der Pfad auf der PS4 ist leicht anders, technisch aber identisch:',
            },
            {
              type: 'list',
              items: [
                'Im PS4-Hauptmenü <strong>Einstellungen → Konto-Verwaltung → Kontoinformationen</strong> öffnen.',
                'Wähle <strong>„Wallet“</strong> und dann <strong>„Add Funds“</strong> bzw. <strong>„Guthaben aufladen“</strong>.',
                'Wähle den Betrag und in der Zahlart-Liste <strong>„Pay By Mobile“</strong> / <strong>„Per Handyrechnung“</strong>.',
                'Mobilfunknummer eintragen, <strong>SMS-Code</strong> bestätigen, fertig.',
              ],
            },
          ],
        },
        {
          id: 'web',
          number: '4',
          heading: 'Im PlayStation-Store-Web',
          blocks: [
            { type: 'paragraph', html: 'Wer den Store am Computer nutzen möchte:' },
            {
              type: 'list',
              items: [
                'Öffne <a href="https://store.playstation.com/de-de/" target="_blank" rel="noopener" class="link link--external"><strong>store.playstation.com/de-de</strong></a> und melde dich mit deinem PSN-Konto an.',
                'Wähle das Spiel, Add-on oder PS-Plus-Abo, das du kaufen willst, und klicke auf <strong>„In den Warenkorb“</strong> → <strong>„Zur Kasse“</strong>.',
                'Reicht dein Wallet-Guthaben nicht aus, bietet der Store an, die Differenz nachzuladen. Wähle dort <strong>„Mobilnummer hinzufügen“</strong> bzw. <strong>„Mobilkonto hinzufügen“</strong>.',
                'Bestätige Telekom Nummer + SMS-Code, der Kauf wird abgeschlossen.',
              ],
            },
            {
              type: 'paragraph',
              html: 'Auf <strong>account.sony.com</strong> selbst kannst du die Bezahlung per Handyrechnung <u>nicht</u> dauerhaft als Default-Zahlungsmethode speichern. Das ist technisch keine echte Default-Methode, sondern wird pro Kauf abgefragt.',
            },
          ],
        },
        {
          id: 'probleme',
          number: '5',
          heading: 'Häufige Fragen und Probleme',
          blocks: [
            {
              type: 'faq',
              items: [
                {
                  question: '„Per Handyrechnung zahlen“ erscheint nicht im Checkout.',
                  answer:
                    'Prüf zuerst, ob deine <strong>PSN-Region</strong> auf Deutschland steht (unter Kontoinformationen). Bei <strong>Reseller-Tarifen</strong> (congstar, freenet, mobilcom-debitel, 1&1 im Telekom Netz) ist die Bezahlung per Handyrechnung für PSN in der Regel nicht freigeschaltet. Außerdem darf die <strong>Drittanbietersperre</strong> in MeinMagenta nicht aktiv sein.',
                },
                {
                  question: 'Mein Kind kann mit seinem PSN-Konto nicht per Telekom zahlen.',
                  answer:
                    'Sub-Accounts (Kinder unter 18) können <strong>keine eigene Zahlung per Handyrechnung</strong> auslösen. Die Abrechnung läuft zwingend über das <strong>Erwachsenen-Hauptkonto</strong> der Familie. Lade als Erwachsener das Wallet auf und lass dein Kind das Guthaben nutzen, oder aktiviere die <strong>Familienausgaben-Funktion</strong> in den PSN-Einstellungen.',
                },
                {
                  question: 'Ich bekomme keine SMS-TAN.',
                  answer:
                    'Warte 60 Sekunden, dann fordere den Code erneut an. Stelle sicher, dass die <strong>Drittanbietersperre</strong> für „digitale Dienste“ in MeinMagenta freigegeben ist. Bei Telekom Prepaid kann es zudem sein, dass dein <strong>aktuelles Prepaid-Guthaben den Wallet-Betrag nicht deckt</strong> — dann wird die Aufladung abgelehnt.',
                },
                {
                  question: 'Geht das auch für PlayStation Plus?',
                  answer:
                    'Ja, in beiden Varianten: Lade dein Wallet per Handyrechnung auf und PS Plus zieht beim nächsten Verlängerungstermin automatisch davon. Direkt im Checkout für PS-Plus-Abos kannst du die Bezahlung per Handyrechnung ebenfalls wählen.',
                },
                {
                  question: 'Wie sehe ich den Posten auf der Telekom Rechnung?',
                  answer:
                    'In <strong>MeinMagenta → Rechnungen → Drittanbieter-Leistungen</strong>. Der Anbieter heißt dort typischerweise <em>„Sony Interactive Entertainment Network Europe Ltd.“</em> mit Datum und Betrag. Auf der Rechnung steht der Aufladungsbetrag, nicht das konkret gekaufte Spiel.',
                },
              ],
            },
          ],
        },
      ],
    },
  },
  {
    cat: 'mobil',
    card: {
      heading: { ...cardHeading, text: 'easypark' },
      text: 'Parkzeit per SMS starten und verlängern, ohne Kleingeld, Automaten oder Parkschein.',
      buttons: [
        {
          tag: 'a',
          href: 'https://www.easypark.com/de',
          isExternal: true,
          label: 'Weiter zu easypark',
          primary: true,
        },
      ],
      image: {
        src: '/content/content-images/dcb/messe/partner/ab27b3f99f1d1b9ed323d1bbd86f8f10/easypark-logo.svg',
        lazy: true,
        alt: 'easypark Logo',
        aspectRatio: '222 / 36',
      },
    },
    anleitung: {
      eyebrow: 'Anleitung · easypark',
      title: 'So zahlst du easypark ',
      titleAccent: 'per Telekom SMS.',
      lead: 'easypark unterstützt aktuell nur SMS-Parken als Variante per Handyrechnung. In der App selbst läuft die Zahlung über klassische Zahlarten (Kreditkarte, PayPal).',
      meta: {
        duration: 'Dauer: ca. 2 Minuten',
        device: 'Gerät: jedes Handy mit SMS-Funktion',
        confirmation: 'Direkt über die Telekom Rechnung',
      },
      sections: [
        {
          id: 'start',
          number: '1',
          heading: 'So funktioniert es bei easypark',
          blocks: [
            {
              type: 'paragraph',
              html: 'Anders als bei PayByPhone bietet die <strong>easypark-App in DE aktuell keine eigene Zahlart per Handyrechnung</strong>. In den App-Zahlungsmethoden sind nur Kreditkarte, PayPal, SEPA-Lastschrift und Apple Pay verfügbar. Wer per Telekom Handyrechnung parken möchte, nutzt stattdessen <strong>SMS-Parken über eine Stadtkurzwahl</strong>. Das funktioniert komplett ohne easypark-App-Account, der Betrag landet direkt auf deiner Telekom Mobilfunkrechnung.',
            },
            { type: 'subheading', text: 'Du brauchst dazu:' },
            {
              type: 'list',
              items: [
                'Einen <strong>Telekom Mobilfunkvertrag</strong>, deaktivierte Drittanbietersperre.',
                'Das <strong>Kennzeichen deines Fahrzeugs</strong>.',
                'Eine SMS-fähige <strong>Stadtkurzwahl</strong>, die am Parkautomaten oder auf einem Hinweisschild ausgewiesen ist.',
              ],
            },
          ],
        },
        {
          id: 'sms',
          number: '2',
          heading: 'Parken per SMS-Kurzwahl',
          blocks: [
            {
              type: 'list',
              items: [
                'Steig aus dem Auto und such am <strong>Parkautomat oder am nächsten Hinweisschild</strong> nach der städtischen SMS-Kurzwahl (z. B. „SMS an 22550 mit Kennzeichen und Parkdauer“).',
                'Schicke eine SMS an die Kurzwahl mit deinem <strong>Kennzeichen und der Parkdauer</strong>, je nach Stadt unterschiedlich formuliert. Beispiel: <strong>B-AB1234 60</strong> (Kennzeichen, Leerzeichen, Minuten).',
                'Du bekommst sofort eine <strong>Bestätigungs-SMS</strong> mit Parkdauer und Preis. Der Parkschein ist aktiv.',
                'Möchtest du verlängern, schicke eine zweite SMS mit der neuen Parkdauer an dieselbe Kurzwahl.',
                'Beim Ausparken nichts weiter tun: Die SMS-Parkzeit läuft automatisch ab, einzelne Städte verlangen ein <strong>„STOP“</strong>-SMS zum vorzeitigen Beenden (oft kostenfrei).',
              ],
            },
          ],
        },
        {
          id: 'probleme',
          number: '3',
          heading: 'Häufige Fragen und Probleme',
          blocks: [
            {
              type: 'faq',
              items: [
                {
                  question: 'Welche Städte unterstützen SMS-Parken?',
                  answer:
                    'Nicht jede Stadt bietet SMS-Parken an. Die offizielle Übersicht findest du auf <a href="https://www.bussgeldkatalog.de/handyparken/" target="_blank" rel="noopener" class="link link--external">bussgeldkatalog.de/handyparken</a>. In Berlin, München, Hamburg, Köln, Frankfurt und vielen kleineren Städten funktioniert es zuverlässig. Wenn am Automaten keine Kurzwahl steht, ist SMS-Parken in dieser Zone nicht aktiv.',
                },
                {
                  question: 'Geht das auch mit Telekom Prepaid?',
                  answer:
                    'Ja, SMS-Parken funktioniert auch mit <strong>Telekom Prepaid</strong>. Voraussetzung: ausreichendes Prepaid-Guthaben für den Parkbetrag und die Premium-SMS-Kosten.',
                },
                {
                  question: 'Wo sehe ich den Posten auf der Telekom Rechnung?',
                  answer:
                    'Bei SMS-Parken erscheint pro SMS eine Premium-SMS-Position direkt im <strong>Verbindungsnachweis</strong> (nicht unter Drittanbieter-Leistungen), meist mit dem Wortlaut <em>„Premium-SMS Parken Stadt XY“</em> oder „SMS Parking“ und dem konkreten Betrag.',
                },
                {
                  question: 'Kann ich bei easypark trotzdem per Handyrechnung zahlen?',
                  answer:
                    'In der App aktuell nicht. Wenn du eine App-basierte Lösung mit echter Carrier-Billing-Zahlart möchtest, nutze <strong>PayByPhone</strong> — dort gibt es die Handyrechnung als reguläre App-Zahlart.',
                },
              ],
            },
          ],
        },
      ],
    },
  },
  {
    cat: 'mobil',
    card: {
      heading: { ...cardHeading, text: 'PayByPhone' },
      text: 'Parkschein bargeldlos per App lösen, in über 400 Städten, mit Verlängerung aus der Ferne.',
      buttons: [
        {
          tag: 'a',
          href: 'https://www.paybyphone.de/',
          isExternal: true,
          label: 'Weiter zu PayByPhone',
          primary: true,
        },
      ],
      image: {
        src: '/content/content-images/dcb/messe/partner/e1d5462a579cef78e0dfd6294e215083/pay-by-phone.png',
        lazy: true,
        alt: 'PayByPhone Logo',
        aspectRatio: '797 / 300',
      },
    },
    anleitung: {
      eyebrow: 'Anleitung · PayByPhone',
      title: 'So zahlst du Parken ',
      titleAccent: 'per Handyrechnung.',
      lead: 'Parkgebühren bargeldlos per PayByPhone-App lösen und die Handyrechnung als Standard-Zahlart hinterlegen. Funktioniert in über 400 Städten.',
      meta: {
        duration: 'Dauer: ca. 4 Minuten',
        device: 'Gerät: iPhone oder Android-Smartphone',
        confirmation: 'Sicher per Telekom SIM',
      },
      sections: [
        {
          id: 'pbp-start',
          number: '1',
          heading: 'Bevor du startest',
          blocks: [
            {
              type: 'paragraph',
              html: 'PayByPhone bietet eigene App-Zahlart <strong>„Telefonrechnung / Phone Bill“</strong>. Dein Parkschein wird beim Kauf direkt über deine Telekom Mobilfunkrechnung abgerechnet, kein Kreditkarten-Hinterlegen, keine zweite App.',
            },
            { type: 'subheading', text: 'Du brauchst dazu:' },
            {
              type: 'list',
              items: [
                'Einen aktiven <strong>Telekom Mobilfunkvertrag</strong>. Reseller (congstar, freenet, mobilcom-debitel, 1&1) sind <u>nicht freigeschaltet</u>.',
                'Die <strong>PayByPhone-App</strong> für iOS oder Android.',
                'In MeinMagenta: <strong>Drittanbietersperre</strong> für „digitale Dienste“ auf <em>„zugelassen“</em>.',
              ],
            },
          ],
        },
        {
          id: 'pbp-neu',
          number: '2',
          heading: 'Du legst ein neues PayByPhone-Konto an',
          blocks: [
            {
              type: 'list',
              items: [
                'Lade die PayByPhone-App und tippe auf <strong>„Konto erstellen“</strong>.',
                'Trage deine <strong>Handynummer</strong>, deine E-Mail-Adresse und ein <strong>Passwort</strong> ein. Ein Kennzeichen wird hier noch nicht abgefragt, das hinterlegst du später pro Fahrzeug.',
                'Bestätige <strong>zuerst deine E-Mail-Adresse</strong> über den Code, den PayByPhone dir schickt. Ohne diese Bestätigung geht es nicht weiter.',
                'Du wirst nicht automatisch zur Zahlart geführt. Tippe unten auf <strong>„Konto“</strong> und dort auf <strong>„Telefonrechnung“</strong> („Add Phone Bill“).',
                'Wähle als <strong>Telefonanbieter Telekom</strong> aus und bestätige den per SMS verschickten Code. Die Telefonrechnung ist jetzt als Zahlart hinterlegt.',
              ],
            },
          ],
        },
        {
          id: 'pbp-bestand',
          number: '3',
          heading: 'Du hast bereits ein PayByPhone-Konto',
          blocks: [
            {
              type: 'list',
              items: [
                'Öffne die PayByPhone-App und tippe unten auf den Tab <strong>„Account“</strong>.',
                'Wähle <strong>„Zahlungsmethoden“</strong>.',
                'Tippe auf <strong>„Telefonrechnung hinzufügen“</strong> bzw. „Add Phone Bill“.',
                'Gib deine Telekom Mobilfunknummer ein und bestätige den per SMS verschickten Code.',
                'Setze Telefonrechnung als <strong>Standardzahlart</strong>, damit künftige Parkvorgänge automatisch darüber laufen.',
              ],
            },
            {
              type: 'hint',
              html: '<b>Tipp:</b> Die hinterlegte Mobilfunknummer kannst du in PayByPhone <u>nicht direkt ändern</u>. Bei Nummer- oder Anbieterwechsel musst du den alten Eintrag erst löschen und dann neu anlegen.',
            },
          ],
        },
        {
          id: 'pbp-probleme',
          number: '4',
          heading: 'Häufige Fragen und Probleme',
          blocks: [
            {
              type: 'faq',
              items: [
                {
                  question: '„Telefonrechnung“ erscheint nicht in der Zahlmethoden-Auswahl.',
                  answer:
                    'Stelle sicher, dass die Region deiner App auf <strong>Deutschland</strong> steht. Bei <strong>Reseller-Tarifen</strong> (congstar etc.) ist die Methode nicht freigeschaltet — PayByPhone unterstützt nur Direkt-Verträge bei Telekom, Vodafone und O2.',
                },
                {
                  question: 'Ich bekomme keinen SMS-Code.',
                  answer:
                    'Prüfe in MeinMagenta unter <strong>Mein Vertrag → Drittanbietersperre → digitale Dienste</strong>, ob die Sperre deaktiviert oder freigegeben ist. Häufigster Grund für ausbleibende SMS-TAN.',
                },
                {
                  question: 'Gibt es ein Limit?',
                  answer:
                    'Ein hartes Monatslimit nennt PayByPhone nicht, in der Praxis greift das Drittanbieter-Cap deines Telekom Tarifs. Bei Power-Parkern empfehlen wir, das Limit beim Telekom Kundenservice zentral prüfen zu lassen.',
                },
                {
                  question: 'SMS-Parken ohne App, geht das auch?',
                  answer:
                    'Ja, in vielen Städten ist <strong>SMS-Parken über eine Stadtkurzwahl</strong> möglich (z. B. SMS mit „KENNZEICHEN:MINUTEN“ an eine 5-stellige Nummer). Das läuft komplett ohne App-Account direkt über deine Mobilfunkrechnung. Welche Stadt welche Kurzwahl nutzt, findest du auf <a href="https://www.paybyphone.de/sms-hilfe" target="_blank" rel="noopener" class="link link--external">paybyphone.de/sms-hilfe</a>.',
                },
                {
                  question: 'Wo sehe ich den Posten auf der Telekom Rechnung?',
                  answer:
                    'In <strong>MeinMagenta → Rechnungen → Drittanbieter-Leistungen</strong>. Der Anbieter heißt dort typischerweise <em>„DIMOCO Payments GmbH“</em> mit Verwendungszweck PayByPhone und Datum.',
                },
              ],
            },
          ],
        },
      ],
    },
  },
  {
    cat: 'mobil',
    card: {
      heading: { ...cardHeading, text: 'Nextbike' },
      text: 'Leihräder in über 60 Städten spontan ausleihen und flexibel zurückgeben, minutengenau abgerechnet.',
      buttons: [
        {
          tag: 'a',
          href: 'https://www.nextbike.de/de/',
          isExternal: true,
          label: 'Weiter zu Nextbike',
          primary: true,
        },
      ],
      image: {
        src: '/content/content-images/dcb/messe/partner/e841542399ba1b8cf22878d48cb4643f/nextbike-logo.svg',
        lazy: true,
        alt: 'Nextbike Logo',
        aspectRatio: '392.2 / 56.5',
      },
    },
    anleitung: {
      eyebrow: 'Anleitung · Nextbike',
      title: 'So fährst du Nextbike ',
      titleAccent: 'und zahlst per Handyrechnung.',
      lead: 'Leihfahrräder in mehr als 300 deutschen Städten per Nextbike-App buchen und die Handyrechnung als Standard-Zahlart hinterlegen.',
      meta: {
        duration: 'Dauer: ca. 4 Minuten',
        device: 'Gerät: iPhone oder Android-Smartphone',
        confirmation: 'Bezahlt über die Telekom SIM',
      },
      sections: [
        {
          id: 'nb-start',
          number: '1',
          heading: 'Bevor du startest',
          blocks: [
            {
              type: 'paragraph',
              html: 'Nextbike bietet seit 2021 eine eigene App-Zahlart <strong>„Zahl einfach per Handyrechnung“</strong>. Du buchst Leihräder in über 60 deutschen Städten, der Betrag landet automatisch auf deiner Telekom Mobilfunkrechnung. Keine Kreditkarte, kein PayPal.',
            },
            { type: 'subheading', text: 'Du brauchst dazu:' },
            {
              type: 'list',
              items: [
                'Einen aktiven <strong>Telekom Mobilfunkvertrag</strong> (Postpaid oder Prepaid). Bei Prepaid erfolgt die Abrechnung über dein Prepaid-Guthaben. <strong>Reseller</strong> (congstar, freenet, mobilcom-debitel, 1&1) sind ausgeschlossen.',
                'Die <strong>nextbike-App</strong> für iOS oder Android.',
                'In MeinMagenta: <strong>Drittanbietersperre</strong> freigeschaltet.',
              ],
            },
          ],
        },
        {
          id: 'nb-neu',
          number: '2',
          heading: 'Du legst ein neues Nextbike-Konto an',
          blocks: [
            {
              type: 'list',
              items: [
                'Lade die nextbike-App und starte die Registrierung. Trage Mail-Adresse, Passwort und deine <strong>Telekom Mobilfunknummer</strong> ein.',
                'Im Schritt <strong>„Zahlungsmethode“</strong> wählst du <strong>„Zahl einfach per Handyrechnung“</strong>.',
                'Bestätige die per SMS verschickte <strong>TAN</strong>.',
                'Fertig. Du kannst direkt das nächste Rad ausleihen, die Abrechnung läuft automatisch über deine Telekom Rechnung.',
              ],
            },
          ],
        },
        {
          id: 'nb-bestand',
          number: '3',
          heading: 'Du hast bereits ein Nextbike-Konto',
          blocks: [
            {
              type: 'list',
              items: [
                'Öffne die nextbike-App und tippe auf das <strong>Menü-Symbol</strong> oben links.',
                'Gehe zu <strong>„Account-Einstellungen“</strong> bzw. <strong>„Einstellungen“</strong>.',
                'Wähle <strong>„Bezahlung“</strong> oder <strong>„Zahlungsmethode“</strong>.',
                'Tippe auf <strong>„Zahl einfach per Handyrechnung“</strong>.',
                'Gib deine Telekom Nummer ein, bestätige den per SMS verschickten Code. Künftige Fahrten werden automatisch über die Handyrechnung abgerechnet.',
              ],
            },
          ],
        },
        {
          id: 'nb-probleme',
          number: '4',
          heading: 'Häufige Fragen und Probleme',
          blocks: [
            {
              type: 'faq',
              items: [
                {
                  question:
                    'Funktioniert die Bezahlung per Handyrechnung auch bei Stadt-Abos wie VRNnextbike?',
                  answer:
                    'Ja. Auch bei Stadt-Tarifen wie VRNnextbike lässt sich die Bezahlung per Handyrechnung hinterlegen, die Bestätigung läuft wie bei regulären Fahrten per SMS-TAN. Nur einzelne Verkehrsverbünde mit komplett eigener Abrechnung können abweichen, dann taucht die Option im Bezahl-Dialog gar nicht erst auf.',
                },
                {
                  question: 'Aktion-Freiminuten erscheinen nicht.',
                  answer:
                    'Bekannter App-Cache-Bug: Aktive Aktion-Freiminuten (z. B. 60 Minuten gratis bei Bezahlung per Handyrechnung-Verknüpfung) werden manchmal erst <strong>nach einem App-Neustart</strong> sichtbar. App schließen, neu öffnen, prüfen.',
                },
                {
                  question: 'Ich bekomme keine SMS-TAN.',
                  answer:
                    'Prüfe die <strong>Drittanbietersperre</strong> in MeinMagenta unter <strong>Mein Vertrag → Drittanbietersperre → digitale Dienste</strong>. Häufigster Grund für ausbleibende TAN.',
                },
                {
                  question: 'Wo sehe ich den Posten auf der Telekom Rechnung?',
                  answer:
                    'In <strong>MeinMagenta → Rechnungen → Drittanbieter-Leistungen</strong>. Der Anbieter heißt dort <em>„DIMOCO Payments GmbH“</em> mit Verwendungszweck Nextbike und Datum.',
                },
              ],
            },
          ],
        },
      ],
    },
  },
  {
    cat: 'mobil',
    card: {
      heading: { ...cardHeading, text: 'CITYWATT' },
      text: 'Energie- und e-Mobility-Lösungen, Laden unterwegs einfach per Handyrechnung.',
      buttons: [
        {
          tag: 'a',
          href: 'https://www.citywatt.de/',
          isExternal: true,
          label: 'Weiter zu CITYWATT',
          primary: true,
        },
      ],
      image: {
        src: '/content/content-images/dcb/messe/partner/1a4e71baeeb272a51c6380fbaa1b8fd4/logo-partner-citywatt.svg',
        lazy: true,
        alt: 'CITYWATT Logo',
        aspectRatio: '158 / 31',
      },
    },
    anleitung: {
      eyebrow: 'Anleitung · CITYWATT',
      title: 'So lädst du dein E-Auto bei CITYWATT ',
      titleAccent: 'per Handyrechnung.',
      lead: 'Ladevorgänge an CITYWATT-Säulen direkt über deine Telekom Mobilfunkrechnung abrechnen. Wird über das MOOVILITY-Backend bestätigt, ohne separate App-Anmeldung.',
      meta: {
        duration: 'Dauer: ca. 3 Minuten',
        device: 'Gerät: iPhone oder Android-Smartphone',
        confirmation: 'TAN-Bestätigung pro Ladevorgang',
      },
      sections: [
        {
          id: 'cw-start',
          number: '1',
          heading: 'So funktioniert es bei CITYWATT',
          blocks: [
            {
              type: 'paragraph',
              html: 'CITYWATT hat <u>keine eigene App-Wallet</u>. Die Bezahlung an einer CITYWATT-Ladesäule läuft entweder per <strong>QR-Code direkt an der Säule</strong> oder über die App des Partners <strong>MOOVILITY</strong> (von Cirrantic). Die Bezahlung per Handyrechnung kommt in beiden Fällen automatisch ins Spiel, sobald du dich mit deiner Telekom Mobilfunknummer ausweist.',
            },
            { type: 'subheading', text: 'Du brauchst dazu:' },
            {
              type: 'list',
              items: [
                'Einen <strong>Telekom Mobilfunk-Laufzeitvertrag</strong> (MagentaMobil XS bis XL). Reseller (congstar, freenet, mobilcom-debitel, 1&1) sind für diesen Partner nicht freigeschaltet.',
                'Smartphone mit Kamera (für den QR-Code) oder die <strong>MOOVILITY-App</strong>.',
                'In MeinMagenta: <strong>Drittanbietersperre</strong> freigegeben.',
              ],
            },
            {
              type: 'hint',
              html: '<b>Wichtig:</b> CITYWATT reserviert pro Ladeversuch <strong>35 €</strong> auf deinem Telekom Verfügungsrahmen. Bei mehreren Fehlversuchen kann das Limit kurzfristig blockieren. Pro Ladevorgang sind maximal <strong>85 kWh</strong> möglich.',
            },
          ],
        },
        {
          id: 'cw-laden',
          number: '2',
          heading: 'Direkt an der Säule laden',
          blocks: [
            {
              type: 'list',
              items: [
                'Stell dein Auto an einer <strong>CITYWATT-Säule</strong> ab. Auf dem Display siehst du einen <strong>QR-Code</strong>.',
                'Scanne den QR-Code mit der Kamera-App deines Smartphones. Du landest auf einer Bezahlseite.',
                'Wähle den Tarif <strong>„Wallet Special / Zahl einfach per Handyrechnung“</strong> aus.',
                'Gib deine <strong>Telekom Mobilfunknummer</strong> ein und bestätige den per SMS verschickten Sicherheitscode.',
                'Stecke den Stecker ein, der Ladevorgang startet. Nach Ende erhältst du per SMS einen Link zur elektronischen Rechnung.',
              ],
            },
          ],
        },
        {
          id: 'cw-app',
          number: '3',
          heading: 'Per MOOVILITY-App laden',
          blocks: [
            {
              type: 'list',
              items: [
                'Lade die <strong>MOOVILITY-App</strong> für iOS oder Android und erlaube den Standort-Zugriff.',
                'Der Filter ist hinter dem <strong>„Drei-Balken“-Button</strong> (Menü) versteckt. Tippe ihn an und filtere nach <strong>„Wallet Special“</strong> bzw. <strong>„CITYWATT“</strong>.',
                'Wähle die nächstliegende <strong>CITYWATT-Säule</strong> aus.',
                'Tippe auf <strong>„Mit Charging Wallet starten“</strong>.',
                'Wähle den <strong>genauen Anschluss</strong> (Ladepunkt) aus, an dem dein Auto steckt.',
                'Wähle als Zahlart <strong>„Zahl einfach per Handyrechnung“</strong>.',
                'Gib deine <strong>Telefonnummer</strong> ein und bestätige sie per SMS-Code. Der Ladevorgang startet, am Ende kommt die Rechnung per Link.',
              ],
            },
          ],
        },
        {
          id: 'cw-probleme',
          number: '4',
          heading: 'Häufige Fragen und Probleme',
          blocks: [
            {
              type: 'faq',
              items: [
                {
                  question:
                    'Mein Vertrag erlaubt die Bezahlung per Handyrechnung an CITYWATT nicht.',
                  answer:
                    'Voraussetzung ist ein <strong>Laufzeitvertrag</strong> (MagentaMobil XS bis XL). Reseller-Tarife (congstar & Co.) sind für diesen Partner nicht freigeschaltet. Auch eine aktive <strong>Drittanbietersperre</strong> blockiert die TAN-Bestätigung.',
                },
                {
                  question: 'Was passiert mit der 35 €-Reservierung?',
                  answer:
                    'CITYWATT reserviert <strong>35 €</strong> pro Ladeversuch auf deinem Telekom Verfügungsrahmen. Nach erfolgreichem Ladevorgang wird die Differenz wieder freigegeben, du zahlst nur den tatsächlichen Strompreis. Bei abgebrochenen Versuchen wird die Reservierung nach ein paar Tagen automatisch zurückgebucht.',
                },
                {
                  question: 'Kann ich mehr als 85 kWh laden?',
                  answer:
                    'Nein, pro Ladevorgang sind <strong>85 kWh</strong> das Maximum. Für Long-Range-EVs ist das gegebenenfalls knapp. Lösung: Zweiter Ladevorgang, der wird als neue Transaktion gestartet (neue 35 €-Reservierung).',
                },
                {
                  question: 'Wo sehe ich den Posten auf der Telekom Rechnung?',
                  answer:
                    'In <strong>MeinMagenta → Rechnungen → Drittanbieter-Leistungen</strong>. Anbieter ist <em>„DIMOCO Payments GmbH“</em> mit Verwendungszweck MOOVILITY/CITYWATT und kWh-Angabe.',
                },
              ],
            },
          ],
        },
      ],
    },
  },
  {
    cat: 'mobil',
    card: {
      heading: { ...cardHeading, text: 'MSU Solutions' },
      text: 'Dienstleister für Energie, Wasser, Wärme und E-Mobilität, mit digitaler Abrechnung.',
      buttons: [
        {
          tag: 'a',
          href: 'https://msu-solutions.de/',
          isExternal: true,
          label: 'Weiter zu MSU',
          primary: true,
        },
      ],
      image: {
        src: '/content/content-images/dcb/messe/partner/dffcac6eca3f179122f11b730b357322/logo-msu-solutions.svg',
        lazy: true,
        alt: 'MSU Solutions Logo',
        aspectRatio: '200 / 110',
      },
    },
    anleitung: {
      eyebrow: 'Anleitung · MSU',
      title: 'So zahlst du bei MSU ',
      titleAccent: 'per Handyrechnung.',
      lead: 'Dienste aus dem MSU-Portfolio direkt über deine Telekom Mobilfunkrechnung bezahlen. Die Bestätigung läuft per m8mit-SMS, ohne separate App-Anmeldung.',
      meta: {
        duration: 'Dauer: ca. 3 Minuten',
        device: 'Gerät: iPhone oder Android-Smartphone',
        confirmation: 'SMS-Bestätigung pro Kauf',
      },
      sections: [
        {
          id: 'start',
          number: '1',
          heading: 'So funktioniert es bei MSU',
          blocks: [
            {
              type: 'paragraph',
              html: 'MSU Solutions ist ein <strong>B2B-Plattformbetreiber</strong>, der das Backend (<em>m8mit-Cloud</em>) für Stadtwerke-Ladesäulen liefert, z. B. Stromnetz Hamburg. Auf dem Display der Säule siehst du nicht „MSU“, sondern den Säulen-Betreiber oder den m8mit-Hinweis. Die Bezahlung per Handyrechnung funktioniert sowohl per <strong>SMS direkt an der Säule</strong> als auch über die <strong>m8mit-App</strong>.',
            },
            { type: 'subheading', text: 'Du brauchst dazu:' },
            {
              type: 'list',
              items: [
                'Einen <strong>Telekom Mobilfunk-Laufzeitvertrag</strong>. Reseller (congstar, freenet, mobilcom-debitel, 1&1) sind für diesen Partner nicht freigeschaltet.',
                'Eine MSU- bzw. m8mit-Säule. Verfügbar vor allem in <strong>Hamburg und ausgewählten Stadtwerke-Netzen</strong>.',
                'In MeinMagenta: <strong>Drittanbietersperre</strong> freigegeben.',
              ],
            },
          ],
        },
        {
          id: 'sms',
          number: '2',
          heading: 'Per SMS an der Ladesäule',
          blocks: [
            {
              type: 'list',
              items: [
                'An der Säule findest du eine <strong>Säulen-ID-Nummer</strong> und die Kurzwahl <strong>678 678</strong> oder <strong>764 764</strong>.',
                'Schicke eine SMS mit der <strong>Säulen-ID</strong> an die Kurzwahl.',
                'Du bekommst eine Rückantwort-SMS mit dem aktuellen Tarif und einer Bestätigungsabfrage.',
                'Antworte mit <strong>OK</strong> oder <strong>JA</strong> (laut Anweisung in der Rückantwort), um den Ladevorgang freizuschalten.',
                'Stecke den Stecker ein, der Strom fließt. Nach Ende bekommst du per SMS einen Link zur Rechnung im <strong>m8mit-Kundenportal</strong>.',
              ],
            },
            {
              type: 'hint',
              html: '<strong>Hinweis:</strong> Ohne Antwort startet die Ladung nicht. Bei manchen Säulen läuft die Belegungs-Uhr trotzdem mit, das kann eine kleine Gebühr verursachen. Antworte daher zügig nach der ersten SMS.',
            },
          ],
        },
        {
          id: 'app',
          number: '3',
          heading: 'Per m8mit-App',
          blocks: [
            {
              type: 'list',
              items: [
                'Lade die <strong>m8mit-App</strong> für iOS oder Android (m8mit.de/app). Im App-Store nach „MSU“ zu suchen funktioniert nicht zuverlässig, suche stattdessen nach „m8mit“.',
                'Finde in der Karte die nächstliegende kompatible Säule (z. B. Stromnetz Hamburg).',
                'Tippe auf <strong>„Laden starten“</strong>, wähle als Zahlart <strong>„Zahl einfach per Handyrechnung“</strong>.',
                'Bestätige Telefonnummer und SMS-Code, fertig.',
              ],
            },
          ],
        },
        {
          id: 'probleme',
          number: '4',
          heading: 'Häufige Fragen und Probleme',
          blocks: [
            {
              type: 'faq',
              items: [
                {
                  question: 'Ich finde keine MSU-App im Store.',
                  answer:
                    'MSU Solutions selbst hat keine eigene Endkunden-App. Suche im App Store nach <strong>„m8mit“</strong> oder nach der Marke des Säulen-Betreibers (z. B. Stromnetz Hamburg). Beide nutzen dasselbe Backend.',
                },
                {
                  question: 'Die Säule unterstützt SMS-Parken nicht.',
                  answer:
                    'Nicht jede Säule läuft auf dem m8mit-Backend. Außerhalb der Partner-Netze (Hamburg, einige Stadtwerke) ist das Verfahren nicht aktiv. Erkennst du an der Säule keine SMS-Kurzwahl und keinen m8mit-Hinweis, weiche auf <strong>CITYWATT-Ladesäulen</strong> aus, die das gleiche Telekom Verfahren über MOOVILITY anbieten.',
                },
                {
                  question: 'Wo sehe ich den Posten auf der Telekom Rechnung?',
                  answer:
                    'In <strong>MeinMagenta → Rechnungen → Drittanbieter-Leistungen</strong>. Anbieter ist <em>„DIMOCO Payments GmbH“</em> mit Verwendungszweck m8mit oder dem Säulen-Betreiber-Namen und Datum.',
                },
                {
                  question: 'Ich bekomme die Rechnung nur als Link, kein PDF.',
                  answer:
                    'Stimmt: Die Detail-Rechnung kommt nicht direkt per SMS-Text, sondern als Link auf das <strong>m8mit-Portal</strong>. Wer kein mobiles Datenvolumen hat, kann das Portal auch zu Hause am Laptop unter <a href="https://www.m8mit.de/" target="_blank" rel="noopener" class="link link--external">m8mit.de</a> aufrufen.',
                },
              ],
            },
          ],
        },
      ],
    },
  },
];

export const partnerApple = partners.find((p) => p.id === 'apple-appstore');
export const partnerGoogle = partners.find((p) => p.id === 'google-playstore');

export const openStoreAnleitung = () => {
  const device = detectDevice.useDetectDevice();
  const partner = device.value === 'Android' ? partnerGoogle : partnerApple;
  if (!partner) return;
  openAnleitung(partner);
};
